import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  Backdrop,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  Chip,
  Paper,
  Divider,
  Stack,
  Autocomplete,
  FormGroup,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  Thermostat as ThermostatIcon,
  Scale as ScaleIcon,
  WaterDrop as WaterDropIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Build as BuildIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Print as PrintIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import ElementSelector from './ElementSelector';
import { clientsService, Client } from '../../services/clientsService';
import { laboratoryMethodsService, LaboratoryMethod } from '../../services/laboratoryMethodsService';
import { useAuth } from '../../contexts/AuthContext';
import { samplesService, generateQRCode } from '../../services/samplesService';
import { SampleDataTransformer } from '../../services/sampleDataTransformer';
import OptimizedSelect from './OptimizedSelect';
import {
  referenceMaterialsService,
  ReferenceMaterial,
} from '../../services/referenceMaterialsService';

// 🧪 Phase 1 — Reception per-sample QA/QC role config (used in Section 2.5).
const QC_ROLE_OPTIONS: Array<{
  value: 'ECHANTILLON' | 'BIS' | 'BLANC' | 'MRC';
  label: string;
  color: string;
  bgColor: string;
}> = [
  { value: 'ECHANTILLON', label: 'Échantillon', color: '#1565c0', bgColor: '#f8f9fa' },
  { value: 'BIS',         label: 'BIS (duplicata)', color: '#ad1457', bgColor: '#fce4ec' },
  { value: 'BLANC',       label: 'Blanc',           color: '#37474f', bgColor: '#eceff1' },
  { value: 'MRC',         label: 'MRC (référence)', color: '#2e7d32', bgColor: '#e8f5e9' },
];


// 🆕 NEW: Label Sheet Configurations for Different Printers
interface LabelSheetConfig {
  name: string;
  cols: number;
  rows: number;
  labelWidth: number; // in pixels at 300 DPI
  labelHeight: number;
  pageWidth: number;
  pageHeight: number;
  marginX: number;
  marginY: number;
  description: string;
}

const LABEL_SHEET_CONFIGS: LabelSheetConfig[] = [
  {
    name: 'Avery 5160',
    cols: 3,
    rows: 10,
    labelWidth: 648, // 2.16" * 300 DPI
    labelHeight: 396, // 1.32" * 300 DPI
    pageWidth: 2480, // 8.27" * 300 DPI (A4)
    pageHeight: 3508, // 11.69" * 300 DPI (A4)
    marginX: 48, // 0.16" margin
    marginY: 180, // 0.6" margin
    description: 'Standard address labels - 30 per sheet'
  },
  {
    name: 'Avery 5162',
    cols: 2,
    rows: 7,
    labelWidth: 972, // 3.24" * 300 DPI
    labelHeight: 504, // 1.68" * 300 DPI
    pageWidth: 2480,
    pageHeight: 3508,
    marginX: 48,
    marginY: 180,
    description: 'Large address labels - 14 per sheet'
  },
  {
    name: 'Brother DK-1209',
    cols: 5,
    rows: 6,
    labelWidth: 496, // 1.65" * 300 DPI (square)
    labelHeight: 496,
    pageWidth: 2480,
    pageHeight: 3508,
    marginX: 0,
    marginY: 150,
    description: 'Small square labels - 30 per sheet'
  },
  {
    name: 'Custom Grid 4x8',
    cols: 4,
    rows: 8,
    labelWidth: 600, // 2" * 300 DPI
    labelHeight: 420, // 1.4" * 300 DPI
    pageWidth: 2480,
    pageHeight: 3508,
    marginX: 40,
    marginY: 120,
    description: 'Custom layout - 32 per sheet'
  }
];

// Default to Avery 5160 (most common)
const DEFAULT_LABEL_SHEET = LABEL_SHEET_CONFIGS[0];

// 🆕 NEW: Roll Printer Configurations (Most Common in Labs)
interface RollLabelConfig {
  name: string;
  width: number; // in pixels at 300 DPI
  height: number;
  description: string;
  printerType: 'roll' | 'sheet';
  mmWidth: number; // actual size in mm
  mmHeight: number;
}

const ROLL_LABEL_CONFIGS: RollLabelConfig[] = [
  {
    name: 'Brother DK-11209 (62×29mm)',
    width: 732, // 62mm * 300DPI / 25.4
    height: 343, // 29mm * 300DPI / 25.4
    mmWidth: 62,
    mmHeight: 29,
    description: 'Standard address label roll - most common',
    printerType: 'roll'
  },
  {
    name: 'Brother DK-11208 (38×90mm)', 
    width: 449, // 38mm * 300DPI / 25.4
    height: 1063, // 90mm * 300DPI / 25.4
    mmWidth: 38,
    mmHeight: 90,
    description: 'Large file folder label - perfect for samples',
    printerType: 'roll'
  },
  {
    name: 'Zebra 54×25mm',
    width: 638, // 54mm * 300DPI / 25.4  
    height: 295, // 25mm * 300DPI / 25.4
    mmWidth: 54,
    mmHeight: 25,
    description: 'Compact thermal label',
    printerType: 'roll'
  },
  {
    name: 'DYMO 89×36mm',
    width: 1051, // 89mm * 300DPI / 25.4
    height: 425, // 36mm * 300DPI / 25.4
    mmWidth: 89,
    mmHeight: 36,
    description: 'Large DYMO label - premium',
    printerType: 'roll'
  }
];

// Default to Brother DK-11209 (most common roll label)
const DEFAULT_ROLL_LABEL = ROLL_LABEL_CONFIGS[0];

// 🆕 HELPER: Load QR code image and wait for it (returns Promise)
const loadQRCodeImage = (sampleId: string, size: number): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const qrImage = new Image();
    qrImage.crossOrigin = 'anonymous';
    
    // Primary API - quickchart.io
    const primaryUrl = `https://quickchart.io/qr?text=${encodeURIComponent(sampleId)}&size=${size}&format=png&margin=2&ecLevel=M`;
    
    qrImage.onload = () => {
      console.log(`✅ QR loaded for: ${sampleId}`);
      resolve(qrImage);
    };
    
    qrImage.onerror = () => {
      console.warn(`Primary QR API failed for ${sampleId}, trying backup...`);
      
      // Backup API - qrserver.com
      const backupImage = new Image();
      backupImage.crossOrigin = 'anonymous';
      const backupUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(sampleId)}&format=png&margin=2&ecc=M`;
      
      backupImage.onload = () => {
        console.log(`✅ Backup QR loaded for: ${sampleId}`);
        resolve(backupImage);
      };
      
      backupImage.onerror = () => {
        console.error(`❌ All QR APIs failed for: ${sampleId}`);
        reject(new Error(`Failed to load QR for ${sampleId}`));
      };
      
      backupImage.src = backupUrl;
    };
    
    qrImage.src = primaryUrl;
  });
};

// 🆕 NEW: Batch QR Printing Function - Single Page with Grid Layout
const printBatchQRCodes = async (
  samples: any[], 
  batchNumber: string,
  qrBlobs: Blob[],
  labelConfig: LabelSheetConfig = DEFAULT_LABEL_SHEET
) => {
  try {
    // Create a single page with all QR codes in grid layout
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) throw new Error('Could not get canvas context');
    
    // Use configurable page dimensions
    canvas.width = labelConfig.pageWidth;
    canvas.height = labelConfig.pageHeight;
    
    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, labelConfig.pageWidth, labelConfig.pageHeight);
    
    // Use configurable grid layout
    const { cols, rows, labelWidth, labelHeight, marginX, marginY } = labelConfig;
    const labelsPerPage = cols * rows;
    
    // Calculate spacing between labels
    const spacingX = (labelConfig.pageWidth - (cols * labelWidth) - (2 * marginX)) / (cols - 1);
    const spacingY = (labelConfig.pageHeight - marginY - (rows * labelHeight)) / (rows + 1);
    
    // Process samples in chunks of labelsPerPage
    const totalPages = Math.ceil(samples.length / labelsPerPage);
    
    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
      // Clear canvas for new page (except first page)
      if (pageIndex > 0) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, labelConfig.pageWidth, labelConfig.pageHeight);
      }
      
      const startIndex = pageIndex * labelsPerPage;
      const endIndex = Math.min(startIndex + labelsPerPage, samples.length);
      const pageSamples = samples.slice(startIndex, endIndex);
      
      // Add page header (only if there's space)
      if (marginY > 100) {
        ctx.fillStyle = '#2c5f2d';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          `AFRILAB MINING - Lot ${batchNumber} (Page ${pageIndex + 1}/${totalPages}) - ${labelConfig.name}`, 
          labelConfig.pageWidth/2, 
          60
        );
      }
      
      // 🆕 FIXED: Pre-load ALL QR codes FIRST, then draw them
      console.log(`⏳ Loading ${pageSamples.length} QR codes...`);
      const qrSize = 180;
      const qrImages: (HTMLImageElement | null)[] = await Promise.all(
        pageSamples.map(async (sample) => {
          try {
            // 🆕 QR contains ONLY sample_id for easy scanning
            return await loadQRCodeImage(sample.sample_id, qrSize);
          } catch (error) {
            console.warn(`Failed to load QR for ${sample.sample_id}:`, error);
            return null;
          }
        })
      );
      console.log(`✅ All QR codes loaded!`);
      
      // Now draw all labels with their QR codes
      pageSamples.forEach((sample, index) => {
        const globalIndex = startIndex + index;
        const row = Math.floor(index / cols);
        const col = index % cols;
        
        // Calculate position
        const x = marginX + col * (labelWidth + spacingX);
        const y = marginY + spacingY + row * (labelHeight + spacingY);
        
        // Draw label border
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, labelWidth, labelHeight);
        
        // Draw QR code (centered in top part of label)
        const labelQrSize = 180;
        const qrX = x + (labelWidth - labelQrSize) / 2;
        const qrY = y + 20;
        
        const qrImage = qrImages[index];
        if (qrImage) {
          // Draw the REAL QR code
          ctx.drawImage(qrImage, qrX, qrY, labelQrSize, labelQrSize);
        } else {
          // Fallback: Draw text-based label if QR failed
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(qrX, qrY, labelQrSize, labelQrSize);
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.strokeRect(qrX, qrY, labelQrSize, labelQrSize);
          ctx.fillStyle = '#000000';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('QR ERROR', qrX + labelQrSize/2, qrY + labelQrSize/2 - 10);
          ctx.font = 'bold 12px Arial';
          ctx.fillText(sample.sample_id, qrX + labelQrSize/2, qrY + labelQrSize/2 + 10);
        }
        
        // Add text information below QR code
        ctx.fillStyle = '#000';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        
        const textX = x + labelWidth / 2;
        let textY = qrY + labelQrSize + 35;
          
          // Sample ID (most important)
          ctx.font = 'bold 28px Arial';
          ctx.fillText(sample.sample_id, textX, textY);
          textY += 35;
          
          // Batch number
          ctx.font = 'bold 20px Arial';
          ctx.fillStyle = '#d32f2f';
          ctx.fillText(`LOT: ${batchNumber}`, textX, textY);
          textY += 30;
          
          // Client reference
          ctx.font = '16px Arial';
          ctx.fillStyle = '#666';
          ctx.fillText(`REF: ${sample.external_id || 'N/A'}`, textX, textY);
          textY += 25;
          
        // Position
        ctx.font = '14px Arial';
        ctx.fillStyle = '#999';
        ctx.fillText(`${globalIndex + 1}/${samples.length}`, textX, textY);
      });
      
      // Print this page
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>AFRILAB QR Codes - Lot ${batchNumber} - Page ${pageIndex + 1}</title>
              <style>
                @page { 
                  size: A4; 
                  margin: 0; 
                }
                body { 
                  margin: 0; 
                  padding: 0; 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  min-height: 100vh;
                }
                img { 
                  max-width: 100%; 
                  max-height: 100vh; 
                  object-fit: contain;
                }
                @media print {
                  body { margin: 0; }
                  img { width: 100%; height: 100vh; object-fit: cover; }
                }
              </style>
            </head>
            <body>
              <img src="${dataUrl}" alt="QR Codes Page ${pageIndex + 1}" />
            </body>
          </html>
        `);
        printWindow.document.close();
        
        // Auto-print after short delay
        setTimeout(() => {
          printWindow.print();
          if (pageIndex === totalPages - 1) {
            // Close print window after last page
            setTimeout(() => printWindow.close(), 1000);
          }
        }, 500);
      }
    }
    
    console.log(`✅ Batch QR printing completed: ${samples.length} labels across ${totalPages} page(s)`);
    return { success: true, totalPages, totalLabels: samples.length };
    
  } catch (error) {
    console.error('Batch QR printing failed:', error);
    throw error;
  }
};

// 🎨 NEW: LARGE TEXT Beautiful Professional Label Design Function (ASYNC - waits for QR)
const createBeautifulLabel = async (
  sample: any,
  batchNumber: string, 
  position: string,
  config: RollLabelConfig
): Promise<HTMLCanvasElement> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Could not get canvas context');
  
  canvas.width = config.width;
  canvas.height = config.height;
  
  // 🎨 Beautiful gradient background
  const gradient = ctx.createLinearGradient(0, 0, 0, config.height);
  gradient.addColorStop(0, '#ffffff');
  gradient.addColorStop(0.1, '#f8f9fa');
  gradient.addColorStop(1, '#e9ecef');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, config.width, config.height);
  
  // 🎨 Professional border with company colors
  const borderGradient = ctx.createLinearGradient(0, 0, config.width, 0);
  borderGradient.addColorStop(0, '#2c5f2d'); // Dark green
  borderGradient.addColorStop(0.5, '#4caf50'); // Green
  borderGradient.addColorStop(1, '#81c784'); // Light green
  
  ctx.strokeStyle = borderGradient;
  ctx.lineWidth = 8;
  ctx.strokeRect(4, 4, config.width - 8, config.height - 8);
  
  // 🎨 Inner elegant border
  ctx.strokeStyle = '#2c5f2d';
  ctx.lineWidth = 2;
  ctx.strokeRect(12, 12, config.width - 24, config.height - 24);
  
  // 📏 Calculate optimal layout based on label dimensions
  const isWide = config.width > config.height;
  const padding = 20;
  
  // 🎨 Company header - MUCH LARGER
  ctx.fillStyle = '#2c5f2d';
  const headerSize = Math.min(config.width * 0.12, config.height * 0.2, 32);
  ctx.font = `bold ${headerSize}px Arial`;
  ctx.textAlign = 'center';
  const headerY = padding + headerSize;
  ctx.fillText('AFRILAB MINING', config.width / 2, headerY);

  // 🧪 Phase 1 — QA/QC role badge in the top-right corner.
  // Reads sample.qc_role + sample.metadonnees.qc.* set by the backend.
  const qcRole: string = sample?.qc_role || 'ECHANTILLON';
  if (qcRole !== 'ECHANTILLON') {
    const qcMeta = sample?.metadonnees?.qc || {};
    const labelText = (() => {
      switch (qcRole) {
        case 'BIS':
          return 'BIS';
        case 'BLANC':
          return qcMeta.blanc_subtype === 'ANALYTIQUE' ? 'BLANC ANA.' : 'BLANC PRÉP.';
        case 'MRC':
          return `MRC ${qcMeta.reference_material_code || ''}`.trim();
        default:
          return qcRole;
      }
    })();
    const bgColor = (() => {
      switch (qcRole) {
        case 'BIS':   return '#ad1457'; // pink-700
        case 'BLANC': return '#37474f'; // blue-grey-800
        case 'MRC':   return '#2e7d32'; // green-800
        default:      return '#1565c0';
      }
    })();
    const badgeFont = Math.max(12, Math.min(headerSize * 0.6, 22));
    ctx.font = `bold ${badgeFont}px Arial`;
    ctx.textAlign = 'right';
    const textWidth = ctx.measureText(labelText).width;
    const badgePadX = 10;
    const badgePadY = 6;
    const badgeW = textWidth + badgePadX * 2;
    const badgeH = badgeFont + badgePadY * 2;
    const badgeX = config.width - padding - badgeW;
    const badgeY = padding;
    // Filled rectangle
    ctx.fillStyle = bgColor;
    ctx.fillRect(badgeX, badgeY, badgeW, badgeH);
    // White outline so the badge is readable on green-tinged labels
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(badgeX, badgeY, badgeW, badgeH);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(labelText, badgeX + badgeW - badgePadX, badgeY + badgeH - badgePadY - 2);
    // Restore default alignment for the rest of the label.
    ctx.textAlign = 'center';
    ctx.fillStyle = '#2c5f2d';
  }
  
  // 🎨 Elegant separator line under header
  const lineY = headerY + 8;
  const lineGradient = ctx.createLinearGradient(padding, lineY, config.width - padding, lineY);
  lineGradient.addColorStop(0, '#4caf50');
  lineGradient.addColorStop(0.5, '#2c5f2d');
  lineGradient.addColorStop(1, '#4caf50');
  ctx.strokeStyle = lineGradient;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(padding, lineY);
  ctx.lineTo(config.width - padding, lineY);
  ctx.stroke();
  
  if (isWide) {
    // 🏞️ WIDE LAYOUT - QR LEFT, TEXT RIGHT - MUCH LARGER EVERYTHING
    const availableHeight = config.height - headerY - 40;
    const qrSize = Math.min(availableHeight * 0.9, config.width * 0.40);
    const qrX = padding + 10;
    const qrY = headerY + 25;
    
    // 🆕 FIXED: Load QR code SYNCHRONOUSLY using helper (waits for image)
    try {
      const qrImage = await loadQRCodeImage(sample.sample_id, Math.floor(qrSize));
      ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
      console.log(`✅ QR drawn for ${sample.sample_id}`);
    } catch (error) {
      // Fallback: Draw text-based placeholder if QR fails
      console.warn(`QR load failed for ${sample.sample_id}, using text fallback`);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(qrX, qrY, qrSize, qrSize);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(qrX, qrY, qrSize, qrSize);
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${qrSize * 0.08}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('QR ERROR', qrX + qrSize/2, qrY + qrSize/2 - 10);
      ctx.font = `bold ${qrSize * 0.06}px Arial`;
      ctx.fillText(sample.sample_id, qrX + qrSize/2, qrY + qrSize/2 + 15);
    }
    
    // 📝 Text area - RIGHT SIDE - HUGE TEXT AND PERFECT ALIGNMENT
    const textStartX = qrX + qrSize + 20;
    const textWidth = config.width - textStartX - padding;
    const textAreaHeight = config.height - headerY - 40;
    let currentY = qrY + 30;
    
    // Calculate HUGE text sizes based on available space
    const labelFontSize = Math.min(textWidth * 0.16, textAreaHeight * 0.18, 32);
    const sampleIdFontSize = Math.min(textWidth * 0.25, textAreaHeight * 0.35, 48);
    const batchFontSize = Math.min(textWidth * 0.14, textAreaHeight * 0.18, 28);
    const refFontSize = Math.min(textWidth * 0.12, textAreaHeight * 0.15, 24);
    
    // ÉCHANTILLON label - HUGE SIZE
    ctx.fillStyle = '#666';
    ctx.font = `bold ${labelFontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText('ÉCHANTILLON:', textStartX, currentY);
    currentY += labelFontSize + 16;
    
    // Sample ID - MASSIVE AND PROMINENT
    ctx.fillStyle = '#d32f2f';
    ctx.font = `bold ${sampleIdFontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(sample.sample_id, textStartX, currentY);
    currentY += sampleIdFontSize + 24;
    
    // Batch with icon - HUGE SIZE
    ctx.fillStyle = '#e65100';
    ctx.font = `bold ${batchFontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText('🔥 LOT:', textStartX, currentY);
    ctx.fillStyle = '#e65100';
    ctx.font = `bold ${batchFontSize}px Arial`;
    const lotWidth = ctx.measureText('🔥 LOT: ').width;
    ctx.fillText(batchNumber, textStartX + lotWidth, currentY);
    currentY += batchFontSize + 20;
    
    // Client reference - HUGE SIZE
    ctx.fillStyle = '#666';
    ctx.font = `bold ${refFontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText('REF:', textStartX, currentY);
    const refWidth = ctx.measureText('REF: ').width;
    ctx.fillText(sample.external_id || 'N/A', textStartX + refWidth, currentY);
    currentY += refFontSize + 16;
    
    // Position - HUGE SIZE
    ctx.fillStyle = '#999';
    ctx.font = `bold ${refFontSize * 0.9}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(`Position: ${position}`, textStartX, currentY);
    
  } else {
    // 📱 NARROW LAYOUT - QR TOP, TEXT BOTTOM - MUCH LARGER
    const availableHeight = config.height - headerY - 40;
    const qrSize = Math.min(config.width * 0.7, availableHeight * 0.50);
    const qrX = (config.width - qrSize) / 2;
    const qrY = headerY + 20;
    
    // 🆕 FIXED: Load QR code SYNCHRONOUSLY using helper (waits for image)
    try {
      const qrImage = await loadQRCodeImage(sample.sample_id, Math.floor(qrSize));
      ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
      console.log(`✅ QR drawn for ${sample.sample_id} (narrow)`);
    } catch (error) {
      // Fallback: Draw text-based placeholder if QR fails
      console.warn(`QR load failed for ${sample.sample_id}, using text fallback`);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(qrX, qrY, qrSize, qrSize);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(qrX, qrY, qrSize, qrSize);
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${qrSize * 0.08}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('QR ERROR', qrX + qrSize/2, qrY + qrSize/2 - 10);
      ctx.font = `bold ${qrSize * 0.06}px Arial`;
      ctx.fillText(sample.sample_id, qrX + qrSize/2, qrY + qrSize/2 + 15);
    }
    
    // 📝 Text area - BELOW QR - HUGE TEXT AND PERFECT ALIGNMENT
    let currentY = qrY + qrSize + 25;
    const remainingHeight = config.height - currentY - padding;
    
    // Calculate HUGE text sizes for narrow layout
    const sampleIdFontSize = Math.min(config.width * 0.16, remainingHeight * 0.40, 36);
    const batchFontSize = Math.min(config.width * 0.12, remainingHeight * 0.28, 26);
    const refFontSize = Math.min(config.width * 0.10, remainingHeight * 0.22, 20);
    
    // Sample ID - MASSIVE AND CENTERED
    ctx.fillStyle = '#d32f2f';
    ctx.font = `bold ${sampleIdFontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(sample.sample_id, config.width / 2, currentY);
    currentY += sampleIdFontSize + 16;
    
    // Batch - HUGE AND CENTERED
    ctx.fillStyle = '#e65100';
    ctx.font = `bold ${batchFontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(`🔥 ${batchNumber}`, config.width / 2, currentY);
    currentY += batchFontSize + 14;
    
    // Reference and position - HUGE
    ctx.fillStyle = '#666';
    ctx.font = `bold ${refFontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(`REF: ${sample.external_id || 'N/A'} | ${position}`, config.width / 2, currentY);
  }
  
  return canvas;
};

// ️ NEW: Roll Printer Function - Individual Beautiful Labels in Sequence
const printRollLabels = async (
  samples: any[],
  batchNumber: string,
  config: RollLabelConfig = DEFAULT_ROLL_LABEL
): Promise<{ success: boolean; totalLabels: number; printMethod: string }> => {
  try {
    console.log(`🖨️ Starting roll label printing: ${samples.length} labels on ${config.name}`);
    
    // 🆕 FIXED: Create all labels with AWAIT (waits for QR codes to load)
    console.log(`⏳ Loading QR codes for ${samples.length} labels...`);
    const labelCanvases = await Promise.all(samples.map(async (sample, index) => {
      const position = `${index + 1}/${samples.length}`;
      // ✨ FIXED: Extract AFL batch number from sample_id (e.g., "AFL-0011" → "AFL-0011")
      const aflBatchNumber = sample.sample_id.includes('-') ? 
        sample.sample_id.split('-').slice(0, 2).join('-') : // For batch samples: AFL-0011-01 → AFL-0011
        sample.sample_id; // For individual samples: AFL-0011 → AFL-0011
      return await createBeautifulLabel(sample, aflBatchNumber, position, config);
    }));
    console.log(`✅ All ${samples.length} QR codes loaded!`);
    
    // Create a print document with all labels for roll printer
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Could not open print window');
    }
    
    // Convert all labels to data URLs
    const labelDataUrls = labelCanvases.map(canvas => canvas.toDataURL('image/png', 1.0));
    
    // Create print-optimized HTML for roll printer
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>AFRILAB Roll Labels - Lot ${batchNumber}</title>
          <style>
            @page {
              size: ${config.mmWidth}mm ${config.mmHeight}mm;
              margin: 0;
            }
            
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }
            
            .label {
              width: ${config.mmWidth}mm;
              height: ${config.mmHeight}mm;
              page-break-after: always;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0;
              padding: 0;
            }
            
            .label:last-child {
              page-break-after: avoid;
            }
            
            .label img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            
            @media print {
              body { margin: 0; padding: 0; }
              .label { 
                width: ${config.mmWidth}mm; 
                height: ${config.mmHeight}mm; 
                margin: 0; 
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${labelDataUrls.map((dataUrl, index) => `
            <div class="label">
              <img src="${dataUrl}" alt="Label ${index + 1}: ${samples[index].sample_id}" />
            </div>
          `).join('')}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Auto-print after short delay
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 2000);
    }, 1000);
    
    console.log(`✅ Roll label printing completed: ${samples.length} labels (${config.name})`);
    return { 
      success: true, 
      totalLabels: samples.length, 
      printMethod: `Roll printer (${config.name})` 
    };
    
  } catch (error) {
    console.error('Roll label printing failed:', error);
    throw error;
  }
};

// 🎯 NEW: Smart Printing Function - Choose Best Method
const printSmartLabels = async (
  samples: any[],
  batchNumber: string,
  printMode: 'roll' | 'sheet' | 'auto' = 'auto'
): Promise<{ success: boolean; totalLabels: number; totalPages?: number; printMethod: string }> => {
  try {
    // 🎯 FIXED: Always use roll printer for consistent beautiful design
    if (printMode === 'auto') {
      // Always prefer roll printer for consistent professional design
      // regardless of batch size (user preference)
      printMode = 'roll';
    }
    
    if (printMode === 'roll') {
      const result = await printRollLabels(samples, batchNumber, DEFAULT_ROLL_LABEL);
      return { ...result, printMethod: `🏷️ ${result.printMethod}` };
    } else {
      const result = await printBatchQRCodes(samples, batchNumber, [], DEFAULT_LABEL_SHEET);
      return { 
        success: true, 
        totalLabels: result.totalLabels,
        totalPages: result.totalPages,
        printMethod: `📄 Sheet printer (${DEFAULT_LABEL_SHEET.name})` 
      };
    }
  } catch (error) {
    console.error('Smart label printing failed:', error);
    throw error;
  }
};

// Updated individual label function (keep for single samples)
const printSampleLabel = async (
  sample: any, 
  qrBlob: Blob, 
  batchNumber: string, 
  samplePosition: number, 
  totalSamples: number
) => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const labelWidth = DEFAULT_ROLL_LABEL.width;
    const labelHeight = DEFAULT_ROLL_LABEL.height;
    
    canvas.width = labelWidth;
    canvas.height = labelHeight;
    
    if (!ctx) throw new Error('Could not get canvas context');
    
    // Professional gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, labelHeight);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, '#f8f9fa');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, labelWidth, labelHeight);
    
    // Professional border
    ctx.strokeStyle = '#2c5f2d';
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, labelWidth - 6, labelHeight - 6);
    
    // Company branding
    ctx.fillStyle = '#2c5f2d';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('AFRILAB MINING', labelWidth/2, 25);
    
    // Sample information
    ctx.fillStyle = '#d32f2f';
    ctx.font = 'bold 22px Arial';
    ctx.fillText(sample.sample_id, labelWidth/2, 50);
    
    ctx.fillStyle = '#666';
    ctx.font = 'bold 16px Arial';
    ctx.fillText(`LOT: ${batchNumber}`, labelWidth/2, 70);
    ctx.fillText(`${samplePosition}/${totalSamples}`, labelWidth/2, 85);
    
    // QR Code placeholder (simplified)
    const qrSize = 80;
    const qrX = (labelWidth - qrSize) / 2;
    const qrY = 100;
    
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.strokeRect(qrX, qrY, qrSize, qrSize);
    
    ctx.fillStyle = '#000';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('QR CODE', qrX + qrSize/2, qrY + qrSize/2);
    
    // Print the label
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>AFRILAB Label - ${sample.sample_id}</title>
            <style>
              @page { size: 62mm 29mm; margin: 0; }
              body { margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
            </style>
          </head>
          <body>
            <img src="${dataUrl}" alt="Label ${sample.sample_id}" />
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        setTimeout(() => printWindow.close(), 1000);
      }, 500);
    }
    
    console.log(`✅ Label prepared for printing: ${sample.sample_id}`);
    
  } catch (error) {
    console.error(`❌ Label printing failed for ${sample.sample_id}:`, error);
    throw error;
  }
};

interface IndividualSampleData {
  external_id: string;
  sample_number: number;
  poids_kg?: number; // Individual weight for each sample
  // Per-sample customization for multi-lab routing
  use_custom_elements?: boolean;  // Toggle for per-sample mode
  selected_elements?: string[];   // Override lot-level elements
  custom_lab_routing?: Record<string, string>;  // Override lot-level routing
  element_method_mapping?: Record<string, string>;  // 🆕 Per-sample element-to-method mapping
  // 🧪 Phase 1 — QA/QC role of this row (per-sample)
  qc_role?: 'ECHANTILLON' | 'BIS' | 'BLANC' | 'MRC';
  qc_blanc_subtype?: 'PREPARATION' | 'ANALYTIQUE';
  parent_sample_number?: number;        // UI-side: position of the sample being duplicated
  reference_material_id?: string;       // UUID of the OREAS/MRC catalog row
  reference_material_label?: string;    // Human label kept on the form for display
}

export interface CompleteSampleReceptionFormData {
  // Basic Information
  sample_id: string;
  external_id?: string;
  client_id: string;
  nom_projet?: string;
  site_minier?: string;
  
  // Sample Classification
  type_echantillon: string;
  priorite: string;
  
  // État des échantillons (Sample State)
  etat_echantillon: 'SOLIDE' | 'LIQUIDE' | 'PULPE';
  
  // Humidité échantillon (Sample Humidity)
  humidite_echantillon: 'HUMIDE' | 'SEC';
  humidite_visible?: boolean;
  
  // Physical Properties
  poids_kg?: number;
  nombre_sacs?: number;
  temperature_reception?: number;
  description_physique?: string;
  couleur?: string;
  texture?: string;
  
  // Échantillonnage (Sampling Method)
  echantillonnage_par: 'AFRILAB' | 'CLIENT';
  methode_collecte?: string;
  collecteur?: string;
  date_collecte?: Date;
  conditions_meteo?: string;
  equipement_utilise?: string;
  
  // Location & GPS
  coordonnees?: {
    latitude?: number;
    longitude?: number;
    elevation?: number;
    utm_x?: number;
    utm_y?: number;
    utm_zone?: string;
  };
  
  // Geological Information
  formation_geologique?: string;
  type_roche?: string;
  alteration?: string;
  mineralisation_visible?: string;
  profondeur_m?: number;
  reference_forage?: string;
  interval_de?: number;
  interval_a?: number;
  
  // Element Selection & Analysis Methods
  selected_elements: string[];
  analyses_demandees: string[];
  priorite_analyse: string;
  delai_souhaite?: number;
  instructions_speciales?: string;
  
  // NEW: Mechanical preparation methods
  mechanical_preparation_methods?: string[];
  
  // 🔬 NEW: Custom multi-lab routing (element → lab mapping)
  custom_lab_routing?: Record<string, string>;
  
  // 🆕 NEW: Element-to-method mapping for correct detection limits
  element_method_mapping?: Record<string, string>;

  // NEW: Batch information
  batch_info?: {
    batch_number: string;
    sample_position: number;
    total_samples: number;
    is_batch_sample: boolean;
  };
  
  // Custom lab reference for BS-xxxxx-26-01 format (legacy lots used R- prefix)
  custom_lab_reference?: string;
  
  // Quality & Documentation
  photos: string[];
  certificat_transport?: string;
  commentaires_geologue?: string;
  commentaires_reception?: string;
  
  // Storage and logistics
  localisation_stockage?: string;
  
  // Order number (from client, displayed on certificate)
  order_number?: string;

  // Reception Date/Time
  date_reception: Date;
}

interface CompleteSampleReceptionFormProps {
  initialData?: Partial<CompleteSampleReceptionFormData>;
  onSubmit: (data: any) => void; // Allow both form data and backend data
  onCancel: () => void;
  loading?: boolean;
}

const CompleteSampleReceptionForm: React.FC<CompleteSampleReceptionFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const { user } = useAuth();
  
  const [formData, setFormData] = useState<CompleteSampleReceptionFormData>(() => {
    const defaultData: CompleteSampleReceptionFormData = {
      sample_id: '',
      client_id: '',
      type_echantillon: 'MINERAI',
      priorite: 'NORMALE',
      etat_echantillon: 'SOLIDE',
      humidite_echantillon: 'SEC',
      echantillonnage_par: 'CLIENT',
      selected_elements: [],
      analyses_demandees: [],
      priorite_analyse: 'NORMALE',
      photos: [],
      coordonnees: {},
      date_reception: new Date(),
      mechanical_preparation_methods: []
    };
    
    return {
      ...defaultData,
      ...(initialData as Partial<CompleteSampleReceptionFormData> || {})
    };
  });

  const [clients, setClients] = useState<Client[]>([]);
  const [mechanicalPrepMethods, setMechanicalPrepMethods] = useState<LaboratoryMethod[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingMethods, setLoadingMethods] = useState(true);
  
  // Batch-related states
  const [batchCreated, setBatchCreated] = useState<any>(null);
  const [showBatchSuccess, setShowBatchSuccess] = useState(false);

  // 🆕 Submission progress + error UX. The batch path does real work in the
  // browser (one create call per sac + QR/label generation), which can take a
  // while — surface a progress bar so the receptionist isn't staring at a frozen
  // screen, and a clear error dialog (e.g. duplicate ID) when something fails.
  const [submitState, setSubmitState] = useState<{
    active: boolean;
    phase: 'creating' | 'printing' | 'done';
    current: number;
    total: number;
  }>({ active: false, phase: 'creating', current: 0, total: 0 });
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Turn a raw backend/network error into a clear French message for the user.
  const describeSubmitError = (error: unknown): string => {
    const raw = error instanceof Error ? error.message : String(error || '');
    const low = raw.toLowerCase();
    if (
      low.includes('exist') ||
      low.includes('duplicate') ||
      low.includes('unique') ||
      low.includes('déjà') ||
      low.includes('409')
    ) {
      return `Cet identifiant d'échantillon existe déjà. Veuillez vérifier la référence externe / l'ID et réessayer.\n\nDétail technique : ${raw}`;
    }
    if (low.includes('network') || low.includes('failed to fetch') || low.includes('timeout')) {
      return `Problème de connexion au serveur. Vérifiez votre réseau et réessayez.\n\nDétail technique : ${raw}`;
    }
    return raw || 'Une erreur inattendue est survenue lors de la soumission.';
  };

  // Submission is in flight when our internal state says so OR the parent passes loading.
  const isSubmitting = submitState.active || loading;
  const [individualSamples, setIndividualSamples] = useState<IndividualSampleData[]>([]);
  const [referencePrefix, setReferencePrefix] = useState<string>('');

  // 🆕 Per-sample method selection modal state
  const [sampleMethodModalOpen, setSampleMethodModalOpen] = useState(false);
  const [sampleMethodModalSampleNumber, setSampleMethodModalSampleNumber] = useState<number>(0);
  const [sampleMethodModalElement, setSampleMethodModalElement] = useState<string>('');
  const [sampleMethodModalLab, setSampleMethodModalLab] = useState<string>('');
  const [sampleMethodModalMethods, setSampleMethodModalMethods] = useState<any[]>([]);
  const [sampleMethodModalLoading, setSampleMethodModalLoading] = useState(false);

  // 🧪 Phase 1 — MRC catalog (loaded once per form mount, refreshed manually).
  const [referenceMaterials, setReferenceMaterials] = useState<ReferenceMaterial[]>([]);
  const [loadingReferenceMaterials, setLoadingReferenceMaterials] = useState(false);
  const loadReferenceMaterials = useCallback(async () => {
    try {
      setLoadingReferenceMaterials(true);
      const list = await referenceMaterialsService.list({ active: true });
      setReferenceMaterials(list || []);
    } catch (err) {
      console.warn('Could not load reference materials catalog:', err);
      setReferenceMaterials([]);
    } finally {
      setLoadingReferenceMaterials(false);
    }
  }, []);
  useEffect(() => {
    loadReferenceMaterials();
  }, [loadReferenceMaterials]);

  // 🧪 Soft QA/QC warning: an MRC only carries certified values for certain
  // OREAS digestions. If the chosen lot methods use a digestion the MRC doesn't
  // certify, warn here — BEFORE the lot is created — so the receptionist can fix
  // it (no blocking, no downstream mess).
  const [mrcWarnings, setMrcWarnings] = useState<Record<number, string[]>>({});
  const runMrcCompatibilityCheck = useCallback(
    async (sampleNumber: number, referenceMaterialId?: string) => {
      const methodCodes: string[] = ((formData as any)?.analyses_demandees || []) as string[];
      const elements: string[] = ((formData as any)?.selected_elements || []) as string[];
      if (!referenceMaterialId || methodCodes.length === 0) {
        setMrcWarnings(prev => ({ ...prev, [sampleNumber]: [] }));
        return;
      }
      try {
        const res = await referenceMaterialsService.checkCompatibility({
          reference_material_id: referenceMaterialId,
          method_codes: methodCodes,
          elements,
        });
        setMrcWarnings(prev => ({ ...prev, [sampleNumber]: res.warnings || [] }));
      } catch {
        // Non-blocking: never prevent reception because the check failed.
        setMrcWarnings(prev => ({ ...prev, [sampleNumber]: [] }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(formData as any)?.analyses_demandees, (formData as any)?.selected_elements],
  );
  
  // 🔧 PERFORMANCE: Memoized duplicate detection - was O(n²) on every render!
  const duplicateRefs = useMemo(() => {
    if (individualSamples.length === 0) return [];
    const refs = individualSamples.map(s => s.external_id);
    return refs.filter((ref, index) => refs.indexOf(ref) !== index);
  }, [individualSamples]);
  
  // 🔧 PERFORMANCE: Memoized empty reference check
  const hasEmptyRefs = useMemo(() => {
    return individualSamples.some(s => !s.external_id.trim());
  }, [individualSamples]);
  
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    clientInfo: true,
    individualSamples: true,
    elementSelection: true,
    mechanicalPrep: false,
    sampleState: false,
    physicalProps: false,
    sampling: false,
    location: false,
    geological: false,
    documentation: false
  });

  // Sample Types for Mining
  const sampleTypes = [
    { value: 'MINERAI', label: 'Minerai Brut' },
    { value: 'CONCENTRE', label: 'Concentré de Minerai' },
    { value: 'STERILE', label: 'Stérile/Waste Rock' },
    { value: 'SOL', label: 'Échantillon de Sol' },
    { value: 'EAU', label: 'Échantillon d\'Eau' },
    { value: 'CAROTTE', label: 'Carotte de Forage' },
    { value: 'ECLAT', label: 'Éclat de Roche' },
    { value: 'TOUT_VENANT', label: 'Tout-Venant/Grab' }
  ];

  // Collection Methods
  const collectionMethods = [
    { value: 'CANAL', label: 'Échantillonnage par Canal' },
    { value: 'GRAB', label: 'Échantillonnage Grab' },
    { value: 'COMPOSITE', label: 'Échantillon Composite' },
    { value: 'SPLIT', label: 'Échantillon Split' },
    { value: 'SYSTEMATIC', label: 'Échantillonnage Systématique' },
    { value: 'RANDOM', label: 'Échantillonnage Aléatoire' }
  ];

  // Load clients
  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoadingClients(true);
        const activeClients = await clientsService.getActiveClients();
        setClients(activeClients);
      } catch (error) {
        console.error('Error loading clients:', error);
        setErrors(prev => ({ ...prev, clients: 'Erreur lors du chargement des clients' }));
      } finally {
        setLoadingClients(false);
      }
    };

    loadClients();
  }, []);

  // Load mechanical preparation methods
  useEffect(() => {
    const loadMechanicalPrepMethods = async () => {
      try {
        setLoadingMethods(true);
        const mechanicalMethods = await laboratoryMethodsService.getMechanicalPrepMethods();
        setMechanicalPrepMethods(mechanicalMethods);
        console.log('✅ Loaded mechanical preparation methods:', mechanicalMethods.length);
      } catch (error) {
        console.error('❌ Error loading mechanical prep methods:', error);
        setErrors(prev => ({ 
          ...prev, 
          methods: 'Erreur lors du chargement des méthodes de préparation' 
        }));
      } finally {
        setLoadingMethods(false);
      }
    };

    loadMechanicalPrepMethods();
  }, []);

  // Generate individual samples when nombre_sacs or prefix changes
  useEffect(() => {
    if (formData.nombre_sacs && formData.nombre_sacs >= 1) {
      const newSamples: IndividualSampleData[] = [];
      for (let i = 1; i <= formData.nombre_sacs; i++) {
        // Check if we already have a sample for this position
        const existingSample = individualSamples.find(sample => sample.sample_number === i);
        
        // Use prefix-based reference system
        const basePrefix = referencePrefix || 'REF';
        const defaultReference = `${basePrefix}-${i.toString().padStart(4, '0')}`;
        
        newSamples.push({
          sample_number: i,
          external_id: existingSample?.external_id || defaultReference,
          poids_kg: existingSample?.poids_kg, // Preserve existing individual weight
          qc_role: existingSample?.qc_role || 'ECHANTILLON',
          qc_blanc_subtype: existingSample?.qc_blanc_subtype,
          parent_sample_number: existingSample?.parent_sample_number,
          reference_material_id: existingSample?.reference_material_id,
          reference_material_label: existingSample?.reference_material_label,
        });
      }
      setIndividualSamples(newSamples);
    } else {
      setIndividualSamples([]);
    }
  }, [formData.nombre_sacs, referencePrefix]);

  // 🔧 PERFORMANCE: Memoized to prevent unnecessary re-renders
  const updateIndividualSampleReference = useCallback((sampleNumber: number, external_id: string) => {
    setIndividualSamples(prev => 
      prev.map(sample => 
        sample.sample_number === sampleNumber 
          ? { ...sample, external_id }
          : sample
      )
    );
  }, []);

  // 🔧 PERFORMANCE: Memoized to prevent unnecessary re-renders
  const updateIndividualSampleWeight = useCallback((sampleNumber: number, poids_kg: number | undefined) => {
    setIndividualSamples(prev => 
      prev.map(sample => 
        sample.sample_number === sampleNumber 
          ? { ...sample, poids_kg }
          : sample
      )
    );
  }, []);

  // 🧪 Phase 1 — Per-sample QA/QC role helpers.
  // Changing the role wipes mutually-exclusive sub-fields so the form never
  // submits a stale BIS parent when the user toggles to MRC (and vice-versa).
  const updateSampleQcRole = useCallback(
    (sampleNumber: number, role: 'ECHANTILLON' | 'BIS' | 'BLANC' | 'MRC') => {
      setIndividualSamples(prev =>
        prev.map(sample =>
          sample.sample_number === sampleNumber
            ? {
                ...sample,
                qc_role: role,
                // 2026: "Préparation mécanique" blanc removed from reception.
                // Mechanical-prep quality is now measured per-sample in the Prep
                // Mécanique dashboard ("Qualité de concassage / broyage"). The
                // only remaining blanc at reception is the analytical (reagent) blank.
                qc_blanc_subtype: role === 'BLANC' ? 'ANALYTIQUE' : undefined,
                parent_sample_number: role === 'BIS' ? sample.parent_sample_number : undefined,
                reference_material_id: role === 'MRC' ? sample.reference_material_id : undefined,
                reference_material_label: role === 'MRC' ? sample.reference_material_label : undefined,
              }
            : sample,
        ),
      );
    },
    [],
  );
  const updateSampleBlancSubtype = useCallback(
    (sampleNumber: number, subtype: 'PREPARATION' | 'ANALYTIQUE') => {
      setIndividualSamples(prev =>
        prev.map(sample =>
          sample.sample_number === sampleNumber ? { ...sample, qc_blanc_subtype: subtype } : sample,
        ),
      );
    },
    [],
  );
  const updateSampleParent = useCallback((sampleNumber: number, parentSampleNumber: number | undefined) => {
    setIndividualSamples(prev =>
      prev.map(sample =>
        sample.sample_number === sampleNumber
          ? { ...sample, parent_sample_number: parentSampleNumber }
          : sample,
      ),
    );
  }, []);
  const updateSampleReferenceMaterial = useCallback(
    (sampleNumber: number, rm: ReferenceMaterial | null) => {
      setIndividualSamples(prev =>
        prev.map(sample =>
          sample.sample_number === sampleNumber
            ? {
                ...sample,
                reference_material_id: rm?.id,
                reference_material_label: rm ? `${rm.code}${rm.matrix ? ' — ' + rm.matrix : ''}` : undefined,
              }
            : sample,
        ),
      );
      // Fire the soft compatibility check (non-blocking).
      void runMrcCompatibilityCheck(sampleNumber, rm?.id);
    },
    [runMrcCompatibilityCheck],
  );

  // Re-run compatibility checks whenever the lot-level methods or elements change
  // (the MRC was selected first, but the analyst may add/change digestion methods after).
  useEffect(() => {
    const mrcSamples = individualSamples.filter(s => s.reference_material_id);
    if (mrcSamples.length === 0) return;
    for (const s of mrcSamples) {
      void runMrcCompatibilityCheck(s.sample_number, s.reference_material_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(formData as any)?.analyses_demandees, (formData as any)?.selected_elements]);

  // Toggle per-sample customization mode
  // 🔧 PERFORMANCE: Memoized to prevent unnecessary re-renders
  const toggleSampleCustomization = useCallback((sampleNumber: number, enabled: boolean) => {
    setIndividualSamples(prev => 
      prev.map(sample => 
        sample.sample_number === sampleNumber 
          ? { 
              ...sample, 
              use_custom_elements: enabled,
              // When enabling, inherit from lot-level; when disabling, clear
              selected_elements: enabled ? [...formData.selected_elements] : undefined,
              custom_lab_routing: enabled ? { ...formData.custom_lab_routing } : undefined
            }
          : sample
      )
    );
  }, [formData.selected_elements, formData.custom_lab_routing]);

  // Update per-sample element selection
  // 🔧 PERFORMANCE: Memoized to prevent unnecessary re-renders
  const updateSampleElements = useCallback((sampleNumber: number, elements: string[]) => {
    setIndividualSamples(prev => 
      prev.map(sample => 
        sample.sample_number === sampleNumber 
          ? { ...sample, selected_elements: elements }
          : sample
      )
    );
  }, []);

  // 🆕 Handle per-sample lab change - opens method selection modal
  const handleSampleLabChange = async (sampleNumber: number, element: string, newLab: string) => {
    console.log(`🔬 Per-sample lab change for sample ${sampleNumber}, ${element} to ${newLab}`);

    // For Physico-Chimique, no method selection needed - set directly
    if (newLab === 'LABORATOIRE_PHYSICO_CHIMIQUE') {
      updateSampleRoutingDirect(sampleNumber, element, newLab, '');
      return;
    }

    // For ICP, SAA, AU - fetch available methods and show modal
    setSampleMethodModalSampleNumber(sampleNumber);
    setSampleMethodModalElement(element);
    setSampleMethodModalLab(newLab);
    setSampleMethodModalLoading(true);
    setSampleMethodModalOpen(true);

    try {
      const response = await fetch(`/api/v1/laboratory-methods/by-element-and-lab/${element}/${newLab}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const methods = await response.json();
        console.log(`✅ Found ${methods.length} methods for ${element} in ${newLab}:`, methods);
        setSampleMethodModalMethods(methods);

        // If only 1 method, auto-select it
        if (methods.length === 1) {
          handleSampleMethodSelect(sampleNumber, element, newLab, methods[0].method_code);
          setSampleMethodModalOpen(false);
        }
      } else {
        console.error('Failed to fetch methods:', response.status);
        setSampleMethodModalMethods([]);
      }
    } catch (error) {
      console.error('Error fetching methods:', error);
      setSampleMethodModalMethods([]);
    } finally {
      setSampleMethodModalLoading(false);
    }
  };

  // 🆕 Handle method selection from per-sample modal
  const handleSampleMethodSelect = (sampleNumber: number, element: string, lab: string, methodCode: string) => {
    console.log(`✅ Per-sample method selected for sample ${sampleNumber}, ${element}: ${methodCode} (lab: ${lab})`);
    updateSampleRoutingDirect(sampleNumber, element, lab, methodCode);
    setSampleMethodModalOpen(false);
  };

  // 🆕 Direct update of sample routing and method mapping
  // 🔧 PERFORMANCE: Memoized to prevent unnecessary re-renders
  const updateSampleRoutingDirect = useCallback((sampleNumber: number, element: string, lab: string, methodCode: string) => {
    setIndividualSamples(prev => 
      prev.map(sample => {
        if (sample.sample_number === sampleNumber) {
          const newRouting = { ...sample.custom_lab_routing, [element]: lab };
          const newMethodMapping = { ...sample.element_method_mapping };
          if (methodCode) {
            newMethodMapping[element] = methodCode;
          }
          return { 
            ...sample, 
            custom_lab_routing: newRouting,
            element_method_mapping: newMethodMapping
          };
        }
        return sample;
      })
    );
  }, []);

  // 🆕 Cancel per-sample method modal
  const handleSampleMethodModalCancel = () => {
    setSampleMethodModalOpen(false);
    setSampleMethodModalMethods([]);
  };

  // Get lab display name helper
  const getLabDisplayName = (lab: string): string => {
    switch(lab) {
      case 'LABORATOIRE_SAA': return 'SAA';
      case 'LABORATOIRE_ICP': return 'ICP';
      case 'LABORATOIRE_AU': return 'AU';
      case 'LABORATOIRE_VOLUMETRIE': return 'VOL';
      case 'LABORATOIRE_PHYSICO_CHIMIQUE': return 'Physico';
      default: return lab;
    }
  };

  // Get lab color helper
  const getLabColor = (lab: string): string => {
    switch(lab) {
      case 'LABORATOIRE_SAA': return '#FF9800';
      case 'LABORATOIRE_ICP': return '#2196F3';
      case 'LABORATOIRE_AU': return '#FFD700';
      case 'LABORATOIRE_VOLUMETRIE': return '#9C27B0';
      case 'LABORATOIRE_PHYSICO_CHIMIQUE': return '#059669';
      default: return '#757575';
    }
  };

  const generateReferencesFromPrefix = () => {
    if (individualSamples.length === 0 || !referencePrefix.trim()) return;
    
    const input = referencePrefix.trim().toUpperCase();
    
    // Check if input ends with digits (like "BMX343445")
    const digitMatch = input.match(/^(.+?)(\d+)$/);
    
    if (digitMatch) {
      // Input has digits at the end - auto-increment mode
      const [, basePart, numberPart] = digitMatch;
      const startNumber = parseInt(numberPart);
      
      setIndividualSamples(prev => 
        prev.map((sample, index) => ({
          ...sample,
          external_id: `${basePart}${(startNumber + index).toString().padStart(numberPart.length, '0')}`
        }))
      );
    } else {
      // Input has no digits - use current logic (PREFIX-0001 format)
      setIndividualSamples(prev => 
        prev.map((sample, index) => ({
          ...sample,
          external_id: `${input}-${(index + 1).toString().padStart(4, '0')}`
        }))
      );
    }
  };

  const autoIncrementReferences = () => {
    if (individualSamples.length === 0) return;
    
    const firstReference = individualSamples[0].external_id;
    const match = firstReference.match(/^(.*-)(\d+)$/);
    
    if (match) {
      const [, prefix, startNum] = match;
      const startNumber = parseInt(startNum);
      
      setIndividualSamples(prev => 
        prev.map((sample, index) => ({
          ...sample,
          external_id: `${prefix}${(startNumber + index).toString().padStart(startNum.length, '0')}`
        }))
      );
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        const parentObj = prev[parent as keyof CompleteSampleReceptionFormData] as any;
        return {
          ...prev,
          [parent]: {
            ...parentObj,
            [child]: value
          }
        };
      }
      return { ...prev, [field]: value };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleElementsChange = useCallback((elements: string[]) => {
    setFormData(prev => ({ ...prev, selected_elements: elements }));
  }, []);

  const handleMethodsChange = useCallback((methods: string[]) => {
    console.log('📝 CompleteSampleReceptionForm - handleMethodsChange called with:', methods);
    
    setFormData(prev => {
      console.log('📝 Current formData.analyses_demandees:', prev.analyses_demandees);
      const newFormData = { ...prev, analyses_demandees: methods };
      console.log('📝 Updated formData.analyses_demandees:', newFormData.analyses_demandees);
      return newFormData;
    });
  }, []);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const validateForm = (): boolean => {
    // Use the centralized validator from SampleDataTransformer
    const validationErrors = SampleDataTransformer.validateFormData(formData);
    
    // Add form-specific validation
    const additionalErrors: string[] = [];
    
    // Element selection validation
    if (formData.selected_elements.length === 0) {
      additionalErrors.push('Au moins un élément doit être sélectionné');
    }

    // Conditional validation for batch samples (including single-sample batches)
    if (formData.nombre_sacs && formData.nombre_sacs >= 1) {
      if (!individualSamples || individualSamples.length === 0) {
        additionalErrors.push('Veuillez définir les références individuelles des échantillons');
      }
    }

    // Combine all errors
    const allErrors = [...validationErrors, ...additionalErrors];
    
    // Convert to object format for display
    const errorObject = allErrors.reduce((acc, error, index) => {
      acc[`error_${index}`] = error;
      return acc;
    }, {} as Record<string, string>);

    setErrors(errorObject);
    return allErrors.length === 0;
  };

  // 🔧 FIX: Made async and properly await handleBatchSubmit
  const handleSubmit = async () => {
    setSubmitError(null);
    if (!validateForm()) {
      return;
    }

    // Treat even 1 sac as a single-sample batch for consistent workflow
    if (formData.nombre_sacs && formData.nombre_sacs >= 1) {
      await handleBatchSubmit();  // 🔧 FIX: Now properly awaited
    } else {
      handleIndividualSubmit();
    }
  };

  const handleIndividualSubmit = () => {
    try {
      // Validate form data
      const validationErrors = SampleDataTransformer.validateFormData(formData);
      if (validationErrors.length > 0) {
        setErrors(validationErrors.reduce((acc, error, index) => {
          acc[`validation_${index}`] = error;
          return acc;
        }, {} as Record<string, string>));
        return;
      }

    // Generate sample ID if not provided
      const sampleId = formData.sample_id || `AFL-${new Date().getFullYear()}-${Date.now().toString().slice(-5)}`;
      
      // ✅ Ensure single samples are still treated as a one-sample batch
      const singleBatchPrefix = formData.batch_info?.batch_number || `BATCH-${new Date().getFullYear()}-${Date.now().toString().slice(-5)}`;
      const singleBatchFormData = {
        ...formData,
        batch_info: {
          batch_number: singleBatchPrefix,
          sample_position: 1,
          total_samples: 1,
          is_batch_sample: true
        }
      };

      // CRITICAL: Transform data properly for backend workflow routing
      const transformedData = SampleDataTransformer.transformToBackendFormat(singleBatchFormData, {
        sample_id: sampleId,
        external_id: formData.external_id || SampleDataTransformer.generateExternalId(formData)
      });

      console.log('🔥 CRITICAL FIX - Sending transformed data to backend:', {
        analyses_demandees: transformedData.analyses_demandees,
        mechanical_preparation_methods: transformedData.mechanical_preparation_methods,
        metadonnees_analyses: transformedData.metadonnees.analyses_demandees
      });

      onSubmit(transformedData);
    } catch (error) {
      console.error('❌ Error in individual submit:', error);
      const message = describeSubmitError(error);
      setSubmitError(message);
      setErrors(prev => ({ ...prev, submit: message }));
    }
  };

  const handleBatchSubmit = async () => {
    const totalToCreate = individualSamples.length || formData.nombre_sacs || 1;
    setSubmitError(null);
    setErrors(prev => ({ ...prev, submit: '' }));
    setSubmitState({ active: true, phase: 'creating', current: 0, total: totalToCreate });
    try {
      console.log('🎯 Creating batch because nombre_sacs =', formData.nombre_sacs);
      
      const samples: any[] = [];
      // 🔧 PERFORMANCE FIX: Removed qrBlobs - QR codes are generated later in printSmartLabels
      // The previous code was generating QR codes TWICE (here and in createBeautifulLabel)
      const nombreSacs = formData.nombre_sacs || 1;
      
      // 🚨 CRITICAL FIX: Generate ONE batch prefix for the entire batch
      const timestamp = Date.now().toString().slice(-5);
      const batchPrefix = `BATCH-${new Date().getFullYear()}-${timestamp}`;
      
      console.log(`🎯 CRITICAL FIX: Using SINGLE batch prefix for all samples: ${batchPrefix}`);
      
      // 🧪 Phase 1 — pre-flight QC validation. Refuse to start the batch if a
      // BIS row is missing its parent or an MRC row is missing its catalog ref.
      const invalidBis = individualSamples.find(
        s => s.qc_role === 'BIS' && !s.parent_sample_number,
      );
      if (invalidBis) {
        throw new Error(
          `Sac #${invalidBis.sample_number}: chaque BIS doit pointer vers un échantillon original du lot.`,
        );
      }
      const invalidMrc = individualSamples.find(
        s => s.qc_role === 'MRC' && !s.reference_material_id,
      );
      if (invalidMrc) {
        throw new Error(
          `Sac #${invalidMrc.sample_number}: chaque MRC doit être lié à un matériau de référence.`,
        );
      }

      // 🆕 NEW: Function to create sample data for batch processing
      // `parentLookup` maps a UI sample_number (BIS) to the persisted UUID of its parent sample.
      const createSampleData = (sampleNumber: number, parentLookup: Map<number, string>) => {
        const sampleNumberFormatted = sampleNumber.toString().padStart(3, '0');
        const individualSample = individualSamples.find(s => s.sample_number === sampleNumber);
        
        // 🔧 FIXED: Use individual weight if specified, otherwise fall back to divided total weight
        const individualWeight = individualSample?.poids_kg || 
          (formData.poids_kg ? formData.poids_kg / nombreSacs : undefined);
        
        // 🔬 Per-sample element/routing support
        // If sample has custom elements enabled, use its specific elements/routing
        // Otherwise, inherit from lot-level selection
        const sampleSelectedElements = individualSample?.use_custom_elements && individualSample.selected_elements
          ? individualSample.selected_elements
          : formData.selected_elements;
        
        const sampleCustomRouting = individualSample?.use_custom_elements && individualSample.custom_lab_routing
          ? individualSample.custom_lab_routing
          : formData.custom_lab_routing;

        // 🆕 Get per-sample element-method mapping
        const sampleElementMethodMapping = individualSample?.use_custom_elements && individualSample.element_method_mapping
          ? individualSample.element_method_mapping
          : formData.element_method_mapping;

        const qcRole = individualSample?.qc_role || 'ECHANTILLON';
        const resolvedParentId = qcRole === 'BIS' && individualSample?.parent_sample_number
          ? parentLookup.get(individualSample.parent_sample_number)
          : undefined;

        console.log(`📦 Sample ${sampleNumber}/${nombreSacs}:`, {
          useCustomElements: individualSample?.use_custom_elements || false,
          elements: sampleSelectedElements,
          routing: sampleCustomRouting,
          methodMapping: sampleElementMethodMapping,
          qcRole,
          parentSampleId: resolvedParentId,
          referenceMaterialId: individualSample?.reference_material_id,
        });

        const individualFormData = {
          ...formData,
          sample_id: `${batchPrefix}-${sampleNumberFormatted}`,
          external_id: individualSample?.external_id || SampleDataTransformer.generateExternalId(formData, sampleNumber),
          poids_kg: individualWeight, // Use individual weight
          // 🔬 Per-sample elements/routing/method override
          selected_elements: sampleSelectedElements,
          custom_lab_routing: sampleCustomRouting,
          element_method_mapping: sampleElementMethodMapping,
          description_physique: `Sac ${sampleNumber}/${nombreSacs} - ${formData.type_echantillon}`,
          commentaires_reception: `Échantillon ${sampleNumber}/${nombreSacs} du lot ${batchPrefix}`,
          batch_info: {
            batch_number: batchPrefix,
            sample_position: sampleNumber,
            total_samples: nombreSacs,
            is_batch_sample: true
          },
          // 🧪 QA/QC role propagation (forwarded by SampleDataTransformer.transformToBackendFormat)
          qc_role: qcRole,
          qc_blanc_subtype: qcRole === 'BLANC' ? individualSample?.qc_blanc_subtype : undefined,
          parent_sample_id: resolvedParentId,
          reference_material_id: qcRole === 'MRC' ? individualSample?.reference_material_id : undefined,
        };
        
        return SampleDataTransformer.transformToBackendFormat(individualFormData, {
          sample_id: `${batchPrefix}-${sampleNumberFormatted}`,
          external_id: individualSample?.external_id || SampleDataTransformer.generateExternalId(formData, sampleNumber)
        });
      };

      // 🚨 CRITICAL: Create samples SEQUENTIALLY to ensure proper batch numbering.
      // 🧪 Phase 1 — TWO-PASS: parents (non-BIS) first so we know their UUIDs,
      // then BIS children that reference those UUIDs. Order is otherwise stable
      // (sample_number ASC) so the AFL-XXXX-NN suffix matches the on-screen sac
      // numbering for the parent samples.
      const passOneNumbers = individualSamples
        .filter(s => (s.qc_role || 'ECHANTILLON') !== 'BIS')
        .map(s => s.sample_number)
        .sort((a, b) => a - b);
      const passTwoNumbers = individualSamples
        .filter(s => s.qc_role === 'BIS')
        .map(s => s.sample_number)
        .sort((a, b) => a - b);

      console.log(
        `🎯 Creating samples in two passes — pass1 (parents): ${passOneNumbers.length}, pass2 (BIS): ${passTwoNumbers.length}`,
      );

      const parentLookup = new Map<number, string>();
      const runOne = async (sampleNumber: number) => {
        try {
          const sampleData = createSampleData(sampleNumber, parentLookup);
          const createdSample = await samplesService.createSample(sampleData);
          samples.push(createdSample);
          parentLookup.set(sampleNumber, createdSample.id);
          setSubmitState(prev => ({ ...prev, current: samples.length }));
          console.log(`✅ Created sample ${sampleNumber}/${nombreSacs}:`, createdSample.sample_id);
        } catch (error) {
          console.error(`❌ Failed to create sample ${sampleNumber}:`, error);
          throw error;
        }
      };

      for (const sampleNumber of passOneNumbers) {
        await runOne(sampleNumber);
      }
      for (const sampleNumber of passTwoNumbers) {
        await runOne(sampleNumber);
      }
      
      console.log(`🎉 All ${nombreSacs} samples created successfully with consistent batch numbering`);
      
      // 🆕 NEW: Smart label printing - automatically chooses best method
      let printResult: { totalPages?: number; totalLabels: number; printMethod: string } = { 
        totalPages: 1, 
        totalLabels: samples.length, 
        printMethod: 'Manual printing' 
      };
      try {
        setSubmitState(prev => ({ ...prev, phase: 'printing' }));
        console.log(`🖨️ Starting smart label printing for ${samples.length} samples...`);
        printResult = await printSmartLabels(samples, batchPrefix, 'auto');
        console.log(`✅ Smart label printing completed: ${printResult.printMethod}`);
      } catch (printError) {
        console.warn(`⚠️ Smart label printing failed:`, printError);
        // Don't fail the batch creation if printing fails
      }
      
      // Show success message for batch
      const totalWeight = samples.reduce((sum, s) => {
        // Get individual weight from sample metadata or fallback
        const sampleWeight = s.metadonnees?.poids_kg || 0;
        return sum + sampleWeight;
      }, 0);
      
      // ✨ Extract AFL batch number from first sample (e.g., "AFL-0062-01" → "AFL-0062")
      const aflBatchNumber = samples.length > 0 && samples[0].sample_id.includes('-') 
        ? samples[0].sample_id.split('-').slice(0, 2).join('-')  // "AFL-0062-01" → "AFL-0062"
        : batchPrefix;  // Fallback to old format if extraction fails
      
      setSubmitState(prev => ({ ...prev, phase: 'done' }));
      setBatchCreated({
        batch: {
          batch_number: aflBatchNumber,  // ✨ Use AFL format instead of old BATCH format
          id: 'generated',
          description: `Lot de ${nombreSacs} échantillons`,
          status: 'ACTIVE',
          created_at: new Date()
        },
        samples,
        summary: {
          totalSamples: samples.length,
          totalWeight,
          averageWeight: totalWeight / samples.length || 0,
          elementsSelected: formData.selected_elements.length,
          methodsSelected: formData.analyses_demandees.length,
          printResult: `${printResult.totalLabels} étiquettes imprimées via ${printResult.printMethod}${printResult.totalPages ? ` sur ${printResult.totalPages} page(s)` : ''}`
        }
      });
      setShowBatchSuccess(true);
      
    } catch (error) {
      console.error('❌ Error creating batch:', error);
      // 🔧 FIX: Don't fall back to individual submit - it closes the form!
      // Instead, show a clear error dialog and let user retry or cancel.
      const message = describeSubmitError(error);
      setSubmitError(message);
      setErrors(prev => ({ ...prev, submit: message }));
    } finally {
      setSubmitState(prev => ({ ...prev, active: false }));
    }
  };

  // Handle mechanical preparation method selection
  const handleMechanicalMethodToggle = (methodCode: string) => {
    const currentMethods = formData.mechanical_preparation_methods || [];
    const newMethods = currentMethods.includes(methodCode)
      ? currentMethods.filter(m => m !== methodCode)
      : [...currentMethods, methodCode];
    
    setFormData(prev => ({ ...prev, mechanical_preparation_methods: newMethods }));
  };

  const handlePrintSummary = () => {
    if (!batchCreated) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Veuillez autoriser les popups pour imprimer');
      return;
    }

    // Prepare the batch data with all necessary information
    const batchData = {
      batchNumber: batchCreated.batch.batch_number,
      client: clients.find(c => c.id === formData.client_id) || { company_name: 'N/A', contact_person: 'N/A', email: 'N/A', phone: 'N/A' },
      samples: batchCreated.samples,
      selectedElements: formData.selected_elements,
      analysesDemandees: formData.analyses_demandees,
      mechanicalPreparationMethods: formData.mechanical_preparation_methods,
      priorite: formData.priorite,
      dateReception: new Date(formData.date_reception),
      recuPar: user?.firstName + ' ' + user?.lastName || 'Réceptionniste'
    };

    // Create a simple, clean HTML document instead of using React components
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Résumé Réception - Lot ${batchData.batchNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              line-height: 1.4;
              color: #333;
              background: white;
              padding: 20px;
            }
            
            @media print {
              body { padding: 10mm; }
              @page { margin: 10mm; size: A4; }
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
            
            .header {
              background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
              color: white;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
              display: flex;
              align-items: center;
              box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
            }
            
            .logo {
              width: 80px;
              height: 80px;
              background: rgba(255, 255, 255, 0.15);
              border-radius: 8px;
              border: 2px solid rgba(255, 255, 255, 0.3);
              margin-right: 20px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
              font-weight: bold;
            }
            
            .header-content {
              flex: 1;
              text-align: center;
            }
            
            .header h1 {
              font-size: 28px;
              margin-bottom: 8px;
              text-shadow: 0 2px 4px rgba(0,0,0,0.3);
            }
            
            .header h2 {
              font-size: 18px;
              margin-bottom: 4px;
              opacity: 0.95;
            }
            
            .header .date {
              font-size: 16px;
              opacity: 0.9;
            }
            
            .status-badge {
              background: rgba(76, 175, 80, 0.2);
              border: 1px solid rgba(76, 175, 80, 0.5);
              border-radius: 4px;
              padding: 8px;
              text-align: center;
              margin-left: 20px;
            }
            
            .status-badge .label {
              font-size: 10px;
              color: #4caf50;
              font-weight: bold;
            }
            
            .status-badge .value {
              font-size: 14px;
              font-weight: bold;
            }
            
            .section {
              background: #f5f5f5;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
              padding: 15px;
              margin-bottom: 15px;
            }
            
            .section-title {
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 10px;
              font-size: 14px;
            }
            
            .client-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }
            
            .info-item {
              font-size: 12px;
            }
            
            .info-label {
              font-weight: bold;
            }
            
            .methods {
              display: flex;
              flex-wrap: wrap;
              gap: 5px;
              margin-top: 10px;
            }
            
            .method-chip {
              background: #4caf50;
              color: white;
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: bold;
            }
            
            .elements-grid {
              display: grid;
              grid-template-columns: repeat(13, 1fr);
              gap: 3px;
              margin-top: 10px;
            }
            
            .element-box {
              background: white;
              border: 1px solid #ddd;
              border-radius: 3px;
              padding: 4px;
              text-align: center;
              font-size: 10px;
              font-weight: bold;
              min-width: 25px;
            }
            
            .samples-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            
            .samples-table th,
            .samples-table td {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
              font-size: 11px;
            }
            
            .samples-table th {
              background: #424242;
              color: white;
              font-weight: bold;
            }
            
            .samples-table tr:nth-child(even) {
              background: #f9f9f9;
            }
            
            .ref-lab {
              color: #1976d2;
              font-weight: bold;
            }
            
            .priority-chip {
              background: #388e3c;
              color: white;
              padding: 2px 6px;
              border-radius: 8px;
              font-size: 9px;
              font-weight: bold;
            }
            
            .signatures {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-top: 20px;
            }
            
            .signature-box {
              background: #f8f9fa;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
              padding: 15px;
            }
            
            .signature-title {
              font-weight: bold;
              color: #666;
              font-size: 10px;
              margin-bottom: 5px;
            }
            
            .signature-line {
              border-bottom: 2px solid #1976d2;
              height: 30px;
              margin-top: 10px;
            }
            
            .footer {
              background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
              border: 1px solid #e0e0e0;
              border-radius: 4px;
              padding: 15px;
              text-align: center;
              margin-top: 20px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            
            .footer-title {
              color: #1976d2;
              font-weight: bold;
              font-size: 14px;
              margin-bottom: 5px;
            }
            
            .footer-subtitle {
              color: #666;
              font-size: 11px;
              margin-bottom: 3px;
            }
            
            .footer-note {
              color: #888;
              font-size: 10px;
              font-style: italic;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">AFL</div>
            <div class="header-content">
              <h1>AFRILAB MINING SERVICES</h1>
              <h2>FICHE DE RÉCEPTION - Lot ${batchData.batchNumber}</h2>
              <div class="date">${batchData.dateReception.toLocaleDateString('fr-FR', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</div>
            </div>
            <div class="status-badge">
              <div class="label">STATUT</div>
              <div class="value">✅ REÇU</div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">INFORMATIONS CLIENT</div>
            <div class="client-info">
              <div class="info-item"><span class="info-label">Société:</span> ${batchData.client.company_name}</div>
              <div class="info-item"><span class="info-label">Contact:</span> ${batchData.client.contact_person}</div>
              <div class="info-item"><span class="info-label">Email:</span> ${batchData.client.email}</div>
              <div class="info-item"><span class="info-label">Téléphone:</span> ${batchData.client.phone}</div>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">MÉTHODES D'ANALYSE</div>
            <div><strong>Analyses Demandées:</strong></div>
            <div class="methods">
              ${batchData.analysesDemandees?.map(method => 
                `<span class="method-chip">${method === 'BGM-MULTI-LAB' ? 'BGM Multi-Laboratoire (ICP + AU)' : method}</span>`
              ).join('') || '<span class="method-chip">Aucune</span>'}
            </div>
          </div>
          
          ${batchData.selectedElements?.length > 0 ? `
          <div class="section">
            <div class="section-title">ÉLÉMENTS SÉLECTIONNÉS (${batchData.selectedElements.length})</div>
            <div class="elements-grid">
              ${batchData.selectedElements.map(element => `<div class="element-box">${element}</div>`).join('')}
            </div>
          </div>
          ` : ''}
          
          <div class="section">
            <div class="section-title">ÉCHANTILLONS (${batchData.samples.length})</div>
            <table class="samples-table">
              <thead>
                <tr>
                  <th style="width: 5%">#</th>
                  <th style="width: 30%">Réf. Lab</th>
                  <th style="width: 30%">Réf. Client</th>
                  <th style="width: 20%">Type</th>
                  <th style="width: 15%">Priorité</th>
                </tr>
              </thead>
              <tbody>
                ${batchData.samples.map((sample, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td class="ref-lab">${sample.sample_id}</td>
                  <td>${sample.external_id || '—'}</td>
                  <td>${sample.type_echantillon || 'MINERAI'}</td>
                  <td><span class="priority-chip">${sample.priorite || batchData.priorite || 'NORMALE'}</span></td>
                </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="signatures">
            <div class="signature-box">
              <div class="signature-title">RÉCEPTION LABORATOIRE</div>
              <div>${batchData.recuPar}</div>
              <div style="font-size: 10px; color: #666;">${batchData.dateReception.toLocaleDateString('fr-FR')} ${batchData.dateReception.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
              <div style="font-size: 10px; color: #666; margin-top: 5px;">Signature:</div>
              <div class="signature-line"></div>
            </div>
            <div class="signature-box">
              <div class="signature-title">VALIDATION CLIENT</div>
              <div style="font-size: 10px; color: #666; margin-top: 5px;">Je confirme la réception et l'exactitude des informations</div>
              <div style="font-size: 10px; color: #666; margin-top: 10px;">Nom et Signature:</div>
              <div class="signature-line" style="border-color: #4caf50;"></div>
            </div>
          </div>
          
          <div class="footer">
            <div class="footer-title">🏭 AFRILAB MINING SERVICES - LABORATOIRE D'ANALYSES MINIÈRES</div>
            <div class="footer-subtitle">Document généré automatiquement par AFRILAB LIMS v2.0</div>
            <div class="footer-note">⚡ Système de Gestion d'Échantillons Miniers - Traçabilité Complète ⚡</div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Auto-print after a short delay
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 500);
  };

  if (loadingClients) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
          <AssignmentIcon sx={{ mr: 2, fontSize: 40 }} />
          📋 Formulaire de Réception d'Échantillon Complet
        </Typography>

        {/* Show validation errors summary */}
        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Veuillez corriger les erreurs suivantes:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* SECTION 1: Informations Client et Propriétés Physiques */}
        <Accordion expanded={expandedSections.clientInfo} onChange={() => toggleSection('clientInfo')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <BusinessIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 1: Informations Client et Propriétés Physiques</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Client Information */}
              <Grid item xs={12}>
                <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2, mb: 2 }}>
                  👤 Informations Client
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <OptimizedSelect
                  label="Client *"
                  value={formData.client_id}
                  onChange={(value) => handleInputChange('client_id', value)}
                  options={clients.map(client => ({
                    value: client.id,
                    label: client.company_name
                  }))}
                  fullWidth
                  required
                  error={!!errors.client_id}
                  helperText={errors.client_id}
                  placeholder="Sélectionner un client..."
                  loading={loadingClients}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Référence Client"
                  value={formData.external_id || ''}
                  onChange={(e) => handleInputChange('external_id', e.target.value)}
                  placeholder="Ex: PRJ-2024-ECH-001"
                  helperText="Référence fournie par le client pour cet échantillon"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="N° de commande"
                  value={(formData as any).order_number || ''}
                  onChange={(e) => handleInputChange('order_number', e.target.value)}
                  placeholder="Ex: BC-2026-001"
                  helperText="Numéro de bon de commande du client (affiché sur le certificat)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Numéro de Lot"
                  value={formData.custom_lab_reference || ''}
                  onChange={(e) => handleInputChange('custom_lab_reference', e.target.value)}
                  placeholder="Ex: 12345 (générera BS-12345-26-01, BS-12345-26-02...)"
                  helperText="Saisissez uniquement le numéro — le préfixe BS et l'année sont ajoutés automatiquement. Laissez vide pour le format AFL."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <OptimizedSelect
                  label="Type d'Échantillon *"
                  value={formData.type_echantillon}
                  onChange={(value) => handleInputChange('type_echantillon', value)}
                  options={sampleTypes.map(type => ({
                    value: type.value,
                    label: type.label
                  }))}
                  fullWidth
                  required
                  error={!!errors.type_echantillon}
                  helperText={errors.type_echantillon}
                  placeholder="Sélectionner le type..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <OptimizedSelect
                  label="Priorité"
                  value={formData.priorite}
                  onChange={(value) => handleInputChange('priorite', value)}
                  options={[
                    { value: "NORMALE", label: "🟢 Normale" },
                    { value: "ELEVEE", label: "🟡 Élevée" },
                    { value: "URGENTE", label: "🔴 Urgente" }
                  ]}
                  fullWidth
                  placeholder="Sélectionner la priorité..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom du Projet"
                  value={formData.nom_projet || ''}
                  onChange={(e) => handleInputChange('nom_projet', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Site Minier"
                  value={formData.site_minier || ''}
                  onChange={(e) => handleInputChange('site_minier', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Date/Heure de Réception *"
                  value={formData.date_reception}
                  onChange={(date) => handleInputChange('date_reception', date || new Date())}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      helperText: "Date et heure de réception au laboratoire"
                    }
                  }}
                />
              </Grid>

              {/* Physical Properties */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2, mb: 2 }}>
                  ⚖️ Propriétés Physiques
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Poids (kg)"
                  type="number"
                  value={formData.poids_kg || ''}
                  onChange={(e) => handleInputChange('poids_kg', parseFloat(e.target.value) || undefined)}
                  error={!!errors.poids_kg}
                  helperText={errors.poids_kg}
                  inputProps={{ step: 0.001, min: 0, max: 1000 }}
                  InputProps={{
                    startAdornment: <ScaleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Nombre de Sacs"
                  type="number"
                  value={formData.nombre_sacs || ''}
                  onChange={(e) => handleInputChange('nombre_sacs', parseInt(e.target.value) || undefined)}
                  inputProps={{ step: 1, min: 0, max: 100 }}
                  helperText={
                    formData.nombre_sacs && formData.nombre_sacs > 1 
                      ? `🎯 MODE LOT: ${formData.nombre_sacs} échantillons seront créés automatiquement`
                      : "Nombre de sacs/contenants reçus"
                  }
                  InputProps={{
                    startAdornment: formData.nombre_sacs && formData.nombre_sacs > 1 ? (
                      <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    ) : (
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: formData.nombre_sacs && formData.nombre_sacs > 1 ? '#e3f2fd' : 'inherit',
                      borderColor: formData.nombre_sacs && formData.nombre_sacs > 1 ? '#1976d2' : 'inherit'
                    }
                  }}
                />
                {formData.nombre_sacs && formData.nombre_sacs > 1 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>🎯 Mode Lot Activé:</strong> Le système créera {formData.nombre_sacs} échantillons 
                      individuels avec des QR codes uniques pour traçabilité.
                    </Typography>
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Température de Réception (°C)"
                  type="number"
                  value={formData.temperature_reception || ''}
                  onChange={(e) => handleInputChange('temperature_reception', parseFloat(e.target.value) || undefined)}
                  error={!!errors.temperature_reception}
                  helperText={errors.temperature_reception}
                  inputProps={{ step: 0.1, min: -50, max: 100 }}
                  InputProps={{
                    startAdornment: <ThermostatIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Couleur"
                  value={formData.couleur || ''}
                  onChange={(e) => handleInputChange('couleur', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Texture"
                  value={formData.texture || ''}
                  onChange={(e) => handleInputChange('texture', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Description Physique"
                  value={formData.description_physique || ''}
                  onChange={(e) => handleInputChange('description_physique', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 2: Sélection des Éléments et Méthodes */}
        <Accordion expanded={expandedSections.elementSelection} onChange={() => toggleSection('elementSelection')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ScienceIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 2: Sélection des Éléments et Méthodes d'Analyse *</Typography>
            {formData.selected_elements.length > 0 && (
              <Chip 
                label={`${formData.selected_elements.length} éléments sélectionnés`}
                size="small" 
                color="success" 
                sx={{ ml: 2 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>⚠️ OBLIGATOIRE:</strong> Vous devez sélectionner au moins un élément et une méthode d'analyse pour pouvoir enregistrer l'échantillon.
              </Typography>
            </Alert>

            {/* ICP shortcuts hint — points to the working built-in "Méthodes Rapides" cards inside ElementSelector */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Pour les analyses ICP 4-Acides ou Fusion :</strong> utilisez les cartes
                <strong> ⚡ Méthodes Rapides</strong> ci-dessous —
                <span style={{ color: '#00BCD4', fontWeight: 'bold' }}> « … Éléments ICP (4-Acides) »</span> pour la digestion 4 acides,
                <span style={{ color: '#E91E63', fontWeight: 'bold' }}> « 32 Éléments ICP (Fusion Na₂O₂) »</span> pour la fusion alcaline.
                Un clic remplit éléments + méthode + routing.
              </Typography>
            </Alert>

            <ElementSelector
              selectedElements={formData.selected_elements}
              onElementsChange={(elements) => handleInputChange('selected_elements', elements)}
              onMethodsChange={handleMethodsChange}
              onCustomRoutingChange={(routing) => handleInputChange('custom_lab_routing', routing)}
              onElementMethodMappingChange={(mapping) => {
                handleInputChange('element_method_mapping', mapping);
                // Defensive: if the user picked elements via the periodic table
                // (without clicking a Méthodes Rapides shortcut) but the
                // ElementSelector resolved a single method per element, copy
                // those methods into `analyses_demandees` so the form can be
                // submitted. The shortcut path already populates this directly
                // via onMethodsChange, so this only fires for manual selection.
                const inferredMethods = Array.from(
                  new Set(Object.values(mapping || {}).filter((m) => !!m))
                );
                if (
                  inferredMethods.length > 0 &&
                  (!formData.analyses_demandees || formData.analyses_demandees.length === 0)
                ) {
                  handleMethodsChange(inferredMethods);
                }
              }}
            />
          </AccordionDetails>
        </Accordion>

        {/* SECTION 2.5: Individual Sample References & Per-Sample Customization (shown for any batch, including 1 sample) */}
        {formData.nombre_sacs && formData.nombre_sacs >= 1 && (
          <Accordion expanded={expandedSections.individualSamples} onChange={() => toggleSection('individualSamples')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <GroupIcon sx={{ mr: 2 }} />
              <Typography variant="h6">
                Section 2.5: Références Individuelles ({formData.nombre_sacs} échantillons)
              </Typography>
              {individualSamples.some(s => s.use_custom_elements) && (
                <Chip 
                  label={`${individualSamples.filter(s => s.use_custom_elements).length} personnalisé(s)`}
                  size="small" 
                  color="warning" 
                  sx={{ ml: 2 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>🎯 Mode Lot Activé:</strong> Entrez un préfixe commun puis générez les références automatiquement 
                  (ex: FR → FR-0001, FR-0002...). Chaque référence doit être unique.<br/>
                  <strong>🔬 Personnalisation:</strong> Vous pouvez personnaliser les éléments et le routage laboratoire pour chaque échantillon individuellement.
                </Typography>
              </Alert>

              {/* Prefix Input & Action buttons */}
              <Box sx={{ mb: 3 }}>
                {/* Prefix Input Field */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Préfixe des Références"
                      value={referencePrefix}
                      onChange={(e) => setReferencePrefix(e.target.value.toUpperCase())}
                      placeholder="Ex: FR, PRJ, MIN, BMX343445"
                      helperText="Préfixe ou référence complète (ex: DRT génère DRT-0001, BMX343445 génère BMX343445, BMX343446...)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff3e0'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={generateReferencesFromPrefix}
                      startIcon={<AutoAwesomeIcon />}
                      disabled={!referencePrefix.trim()}
                      sx={{ height: '56px' }}
                    >
                      Générer {referencePrefix ? `${referencePrefix}-0001...` : 'Références'}
                    </Button>
                  </Grid>
                </Grid>

                {/* Other Action buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={autoIncrementReferences}
                    startIcon={<AutoAwesomeIcon />}
                  >
                    Auto-Incrémenter
                  </Button>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {individualSamples.map((sample) => {
                  const qcRole = sample.qc_role || 'ECHANTILLON';
                  const qcMeta = QC_ROLE_OPTIONS.find(o => o.value === qcRole) || QC_ROLE_OPTIONS[0];
                  const isQcRow = qcRole !== 'ECHANTILLON';
                  // Eligible parents for a BIS row: same batch, must be ECHANTILLON.
                  const eligibleParents = individualSamples.filter(
                    s => s.sample_number !== sample.sample_number && (s.qc_role || 'ECHANTILLON') === 'ECHANTILLON',
                  );
                return (
                  <Grid item xs={12} sm={6} md={4} key={sample.sample_number}>
                    <Paper sx={{ 
                      p: 2, 
                      backgroundColor: isQcRow ? qcMeta.bgColor : (sample.use_custom_elements ? '#fff3e0' : '#f8f9fa'),
                      border: isQcRow
                        ? `2px solid ${qcMeta.color}`
                        : (sample.use_custom_elements ? '2px solid #FF9800' : '1px solid #e0e0e0'),
                      transition: 'all 0.3s ease'
                    }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                        Sac #{sample.sample_number}
                        {isQcRow && (
                          <Chip
                            label={qcMeta.label}
                            size="small"
                            sx={{ ml: 1, backgroundColor: qcMeta.color, color: '#fff', fontWeight: 700 }}
                          />
                        )}
                        {sample.use_custom_elements && (
                          <Chip label="Personnalisé" size="small" color="warning" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      
                      {/* Reference Field */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Référence Client"
                        value={sample.external_id}
                        onChange={(e) => updateIndividualSampleReference(sample.sample_number, e.target.value)}
                        placeholder={referencePrefix ? `${referencePrefix}-${sample.sample_number.toString().padStart(4, '0')}` : `Ex: FR-${sample.sample_number.toString().padStart(4, '0')}`}
                        sx={{ mb: 2 }}
                      />

                      {/* 🧪 QA/QC role + dependent fields */}
                      <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          label="Type"
                          value={qcRole}
                          onChange={(e) =>
                            updateSampleQcRole(
                              sample.sample_number,
                              e.target.value as 'ECHANTILLON' | 'BIS' | 'BLANC' | 'MRC',
                            )
                          }
                        >
                          {QC_ROLE_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <span style={{
                                display: 'inline-block',
                                width: 10, height: 10, borderRadius: '50%',
                                backgroundColor: opt.color, marginRight: 8,
                              }} />
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {qcRole === 'BIS' && (
                        <FormControl size="small" fullWidth sx={{ mb: 1.5 }} error={!sample.parent_sample_number}>
                          <InputLabel>Duplicata de</InputLabel>
                          <Select
                            label="Duplicata de"
                            value={sample.parent_sample_number ?? ''}
                            onChange={(e) =>
                              updateSampleParent(
                                sample.sample_number,
                                e.target.value === '' ? undefined : Number(e.target.value),
                              )
                            }
                          >
                            <MenuItem value=""><em>— Choisir l'échantillon original —</em></MenuItem>
                            {eligibleParents.map(p => (
                              <MenuItem key={p.sample_number} value={p.sample_number}>
                                Sac #{p.sample_number} — {p.external_id || '(sans réf.)'}
                              </MenuItem>
                            ))}
                          </Select>
                          {!sample.parent_sample_number && (
                            <FormHelperText>Sélectionnez l'échantillon original.</FormHelperText>
                          )}
                        </FormControl>
                      )}

                      {qcRole === 'BLANC' && (
                        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                          <InputLabel>Sous-type</InputLabel>
                          <Select
                            label="Sous-type"
                            value={sample.qc_blanc_subtype || 'ANALYTIQUE'}
                            onChange={(e) =>
                              updateSampleBlancSubtype(
                                sample.sample_number,
                                e.target.value as 'PREPARATION' | 'ANALYTIQUE',
                              )
                            }
                          >
                            {/* "Préparation mécanique" blanc removed (2026): mechanical-prep
                                quality is now measured per-sample in the Prep Mécanique dashboard. */}
                            <MenuItem value="ANALYTIQUE">Analytique (réactifs)</MenuItem>
                          </Select>
                        </FormControl>
                      )}

                      {qcRole === 'MRC' && (
                        <Box sx={{ mb: 1.5 }}>
                          <Autocomplete
                            size="small"
                            options={referenceMaterials}
                            loading={loadingReferenceMaterials}
                            value={referenceMaterials.find(rm => rm.id === sample.reference_material_id) || null}
                            onChange={(_, value) => updateSampleReferenceMaterial(sample.sample_number, value)}
                            getOptionLabel={(rm) => rm ? `${rm.code}${rm.matrix ? ' — ' + rm.matrix : ''}` : ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="MRC / OREAS"
                                placeholder="Tapez ex: 415, OREAS 22i…"
                                error={!sample.reference_material_id}
                                helperText={
                                  !sample.reference_material_id
                                    ? 'Choisissez un MRC du catalogue.'
                                    : undefined
                                }
                              />
                            )}
                          />
                          <Typography variant="caption" color="text.secondary">
                            MRC introuvable ?{' '}
                            <a
                              href="/admin/reference-materials"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Ajouter au catalogue
                            </a>
                          </Typography>
                          {(mrcWarnings[sample.sample_number] || []).length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                Attention QA/QC — valeur certifiée OREAS manquante :
                              </Typography>
                              {(mrcWarnings[sample.sample_number] || []).map((w, i) => (
                                <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                                  • {w}
                                </Typography>
                              ))}
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                Vous pouvez quand même réceptionner, mais aucun graphique de récupération MRC ne sera calculé pour ces cas.
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      )}
                      
                      {/* Per-sample customization toggle */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sample.use_custom_elements || false}
                            onChange={(e) => toggleSampleCustomization(sample.sample_number, e.target.checked)}
                            size="small"
                            color="warning"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: sample.use_custom_elements ? 'bold' : 'normal' }}>
                            Personnaliser éléments/laboratoires
                          </Typography>
                        }
                        sx={{ mb: 1 }}
                      />
                      
                      {/* Per-sample element and routing selection */}
                      {sample.use_custom_elements && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff', borderRadius: 1, border: '1px dashed #FF9800' }}>
                          {/* Element selection chips */}
                          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                            Éléments pour cet échantillon:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {formData.selected_elements.map(element => {
                              const isSelected = sample.selected_elements?.includes(element);
                              return (
                                <Chip
                                  key={element}
                                  label={element}
                                  size="small"
                                  variant={isSelected ? "filled" : "outlined"}
                                  color={isSelected ? "primary" : "default"}
                                  onClick={() => {
                                    const currentElements = sample.selected_elements || [];
                                    const newElements = isSelected
                                      ? currentElements.filter(e => e !== element)
                                      : [...currentElements, element];
                                    updateSampleElements(sample.sample_number, newElements);
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              );
                            })}
                          </Box>
                          
                          {/* Lab routing for selected elements */}
                          {(sample.selected_elements?.length || 0) > 0 && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                                Routage laboratoire:
                              </Typography>
                              <Grid container spacing={1}>
                                {sample.selected_elements?.map(element => (
                                  <Grid item xs={6} key={element}>
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column',
                                      gap: 0.5,
                                      p: 0.5,
                                      borderRadius: 0.5,
                                      backgroundColor: alpha(getLabColor(sample.custom_lab_routing?.[element] || 'LABORATOIRE_ICP'), 0.1)
                                    }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold', minWidth: 25 }}>
                                          {element}:
                                        </Typography>
                                        <FormControl size="small" sx={{ flex: 1, minWidth: 60 }}>
                                          <Select
                                            value={sample.custom_lab_routing?.[element] || 'LABORATOIRE_ICP'}
                                            onChange={(e) => handleSampleLabChange(sample.sample_number, element, e.target.value)}
                                            sx={{ 
                                              fontSize: '0.7rem',
                                              '& .MuiSelect-select': { py: 0.25, px: 0.5 }
                                            }}
                                          >
                                            <MenuItem value="LABORATOIRE_SAA" sx={{ fontSize: '0.75rem' }}>SAA</MenuItem>
                                            <MenuItem value="LABORATOIRE_ICP" sx={{ fontSize: '0.75rem' }}>ICP</MenuItem>
                                            <MenuItem value="LABORATOIRE_AU" sx={{ fontSize: '0.75rem' }}>AU</MenuItem>
                                            <MenuItem value="LABORATOIRE_PHYSICO_CHIMIQUE" sx={{ fontSize: '0.75rem' }}>Physico</MenuItem>
                                          </Select>
                                        </FormControl>
                                      </Box>
                                      {/* 🆕 Show selected method for this element */}
                                      {sample.element_method_mapping?.[element] && (
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            fontSize: '0.65rem', 
                                            color: 'text.secondary',
                                            backgroundColor: alpha(getLabColor(sample.custom_lab_routing?.[element] || 'LABORATOIRE_ICP'), 0.15),
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: 0.5,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          📋 {sample.element_method_mapping[element]}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Info when not customized */}
                      {!sample.use_custom_elements && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            Utilisera les éléments du lot.
                        </Typography>
                      </Alert>
                      )}
                    </Paper>
                  </Grid>
                );
                })}
              </Grid>

              {/* Validation warnings - 🔧 PERFORMANCE: Using memoized values */}
              {individualSamples.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Check for duplicates - using memoized duplicateRefs */}
                  {duplicateRefs.length > 0 && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        ⚠️ Références dupliquées détectées: {duplicateRefs.join(', ')}
                      </Typography>
                    </Alert>
                  )}
                  
                  {/* Check for empty references - using memoized hasEmptyRefs */}
                  {hasEmptyRefs && (
                    <Alert severity="warning">
                      <Typography variant="body2">
                        ⚠️ Certaines références sont vides. Toutes les références doivent être renseignées.
                      </Typography>
                    </Alert>
                  )}

                  {/* 🧪 QA/QC sanity checks */}
                  {individualSamples.some(s => s.qc_role === 'BIS' && !s.parent_sample_number) && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        ⚠️ Chaque BIS doit pointer sur un échantillon original du même lot.
                      </Typography>
                    </Alert>
                  )}
                  {individualSamples.some(s => s.qc_role === 'MRC' && !s.reference_material_id) && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        ⚠️ Chaque MRC doit être lié à un matériau de référence du catalogue.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* SECTION 3: Méthodes de Préparation Mécanique */}
        <Accordion expanded={expandedSections.mechanicalPrep} onChange={() => toggleSection('mechanicalPrep')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <BuildIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 3: Méthodes de Préparation Mécanique</Typography>
            {formData.mechanical_preparation_methods && formData.mechanical_preparation_methods.length > 0 && (
              <Chip 
                label={`${formData.mechanical_preparation_methods.length} méthodes`}
                size="small" 
                color="info" 
                sx={{ ml: 2 }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Sélectionnez les méthodes de préparation mécanique nécessaires avant l'analyse chimique.
              </Typography>
            </Alert>
            
            {loadingMethods ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={30} />
                <Typography sx={{ ml: 2 }}>Chargement des méthodes...</Typography>
              </Box>
            ) : (
                <Grid container spacing={2}>
                  {mechanicalPrepMethods.map((method) => (
                  <Grid item xs={12} sm={6} md={4} key={method.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                          checked={formData.mechanical_preparation_methods?.includes(method.method_code) || false}
                              onChange={() => handleMechanicalMethodToggle(method.method_code)}
                            />
                          }
                          label={
                            <Box>
                          <Typography variant="body2" fontWeight="bold">
                                {method.method_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                            {method.method_code}
                              </Typography>
                          {method.description && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {method.description}
                            </Typography>
                              )}
                            </Box>
                          }
                        />
                    </Grid>
                  ))}
                </Grid>
            )}
          </AccordionDetails>
        </Accordion>

        {/* SECTION 4: État des Échantillons */}
        <Accordion expanded={expandedSections.sampleState} onChange={() => toggleSection('sampleState')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <WaterDropIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 4: État des Échantillons</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 2 }}>État de l'échantillon *</FormLabel>
                  <RadioGroup
                    row
                    value={formData.etat_echantillon}
                    onChange={(e) => handleInputChange('etat_echantillon', e.target.value)}
                  >
                    <FormControlLabel value="SOLIDE" control={<Radio />} label="🪨 Solide" />
                    <FormControlLabel value="LIQUIDE" control={<Radio />} label="💧 Liquide" />
                    <FormControlLabel value="PULPE" control={<Radio />} label="🥤 Pulpe" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 2 }}>Humidité de l'échantillon *</FormLabel>
                  <RadioGroup
                    row
                    value={formData.humidite_echantillon}
                    onChange={(e) => handleInputChange('humidite_echantillon', e.target.value)}
                  >
                    <FormControlLabel value="SEC" control={<Radio />} label="🌵 Sec" />
                    <FormControlLabel value="HUMIDE" control={<Radio />} label="💦 Humide" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.humidite_visible || false}
                      onChange={(e) => handleInputChange('humidite_visible', e.target.checked)}
                    />
                  }
                  label="Humidité visible à l'œil nu"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 5: Échantillonnage */}
        <Accordion expanded={expandedSections.sampling} onChange={() => toggleSection('sampling')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <ScheduleIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 5: Informations d'Échantillonnage</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <FormLabel component="legend" sx={{ mb: 2 }}>Échantillonnage effectué par *</FormLabel>
                  <RadioGroup
                    row
                    value={formData.echantillonnage_par}
                    onChange={(e) => handleInputChange('echantillonnage_par', e.target.value)}
                  >
                    <FormControlLabel value="AFRILAB" control={<Radio />} label="🏢 AFRILAB" />
                    <FormControlLabel value="CLIENT" control={<Radio />} label="👤 Client" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <OptimizedSelect
                  label="Méthode de Collecte"
                  value={formData.methode_collecte || ''}
                  onChange={(value) => handleInputChange('methode_collecte', value)}
                  options={collectionMethods.map(method => ({
                    value: method.value,
                    label: method.label
                  }))}
                  fullWidth
                  placeholder="Sélectionner une méthode..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Collecteur"
                  value={formData.collecteur || ''}
                  onChange={(e) => handleInputChange('collecteur', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Date/Heure de Collecte"
                  value={formData.date_collecte}
                  onChange={(date) => handleInputChange('date_collecte', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: "Date et heure de prélèvement sur le terrain"
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Conditions Météo"
                  value={formData.conditions_meteo || ''}
                  onChange={(e) => handleInputChange('conditions_meteo', e.target.value)}
                  placeholder="Ensoleillé, pluvieux, venteux..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Équipement Utilisé"
                  value={formData.equipement_utilise || ''}
                  onChange={(e) => handleInputChange('equipement_utilise', e.target.value)}
                  placeholder="Marteau, pelle, foreuse..."
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 6: Localisation GPS */}
        <Accordion expanded={expandedSections.location} onChange={() => toggleSection('location')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <LocationOnIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 6: Localisation GPS</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={formData.coordonnees?.latitude || ''}
                  onChange={(e) => handleInputChange('coordonnees.latitude', parseFloat(e.target.value) || undefined)}
                  error={!!errors['coordonnees.latitude']}
                  helperText={errors['coordonnees.latitude'] || "Format: 33.123456"}
                  inputProps={{ step: 0.000001, min: -90, max: 90 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={formData.coordonnees?.longitude || ''}
                  onChange={(e) => handleInputChange('coordonnees.longitude', parseFloat(e.target.value) || undefined)}
                  error={!!errors['coordonnees.longitude']}
                  helperText={errors['coordonnees.longitude'] || "Format: -7.123456"}
                  inputProps={{ step: 0.000001, min: -180, max: 180 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Élévation (m)"
                  type="number"
                  value={formData.coordonnees?.elevation || ''}
                  onChange={(e) => handleInputChange('coordonnees.elevation', parseFloat(e.target.value) || undefined)}
                  inputProps={{ step: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="UTM X"
                  type="number"
                  value={formData.coordonnees?.utm_x || ''}
                  onChange={(e) => handleInputChange('coordonnees.utm_x', parseFloat(e.target.value) || undefined)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="UTM Y"
                  type="number"
                  value={formData.coordonnees?.utm_y || ''}
                  onChange={(e) => handleInputChange('coordonnees.utm_y', parseFloat(e.target.value) || undefined)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Zone UTM"
                  value={formData.coordonnees?.utm_zone || ''}
                  onChange={(e) => handleInputChange('coordonnees.utm_zone', e.target.value)}
                  placeholder="29N, 30N..."
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 7: Informations Géologiques */}
        <Accordion expanded={expandedSections.geological} onChange={() => toggleSection('geological')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DescriptionIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 7: Informations Géologiques</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Formation Géologique"
                  value={formData.formation_geologique || ''}
                  onChange={(e) => handleInputChange('formation_geologique', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Type de Roche"
                  value={formData.type_roche || ''}
                  onChange={(e) => handleInputChange('type_roche', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Altération"
                  value={formData.alteration || ''}
                  onChange={(e) => handleInputChange('alteration', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Minéralisation Visible"
                  value={formData.mineralisation_visible || ''}
                  onChange={(e) => handleInputChange('mineralisation_visible', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Profondeur (m)"
                  type="number"
                  value={formData.profondeur_m || ''}
                  onChange={(e) => handleInputChange('profondeur_m', parseFloat(e.target.value) || undefined)}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Référence Forage"
                  value={formData.reference_forage || ''}
                  onChange={(e) => handleInputChange('reference_forage', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Intervalle De (m)"
                  type="number"
                  value={formData.interval_de || ''}
                  onChange={(e) => handleInputChange('interval_de', parseFloat(e.target.value) || undefined)}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Intervalle À (m)"
                  type="number"
                  value={formData.interval_a || ''}
                  onChange={(e) => handleInputChange('interval_a', parseFloat(e.target.value) || undefined)}
                  inputProps={{ step: 0.1, min: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Commentaires Géologue"
                  value={formData.commentaires_geologue || ''}
                  onChange={(e) => handleInputChange('commentaires_geologue', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 8: Documentation */}
        <Accordion expanded={expandedSections.documentation} onChange={() => toggleSection('documentation')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <DescriptionIcon sx={{ mr: 2 }} />
            <Typography variant="h6">Section 8: Documentation et Commentaires</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <OptimizedSelect
                  label="Priorité d'Analyse"
                  value={formData.priorite_analyse}
                  onChange={(value) => handleInputChange('priorite_analyse', value)}
                  options={[
                    { value: "NORMALE", label: "🟢 Normale" },
                    { value: "ELEVEE", label: "🟡 Élevée" },
                    { value: "URGENTE", label: "🔴 Urgente" }
                  ]}
                  fullWidth
                  placeholder="Sélectionner la priorité..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Délai Souhaité (jours)"
                  type="number"
                  value={formData.delai_souhaite || ''}
                  onChange={(e) => handleInputChange('delai_souhaite', parseInt(e.target.value) || undefined)}
                  inputProps={{ step: 1, min: 1, max: 30 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Instructions Spéciales"
                  value={formData.instructions_speciales || ''}
                  onChange={(e) => handleInputChange('instructions_speciales', e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Instructions particulières pour l'analyse..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Commentaires de Réception"
                  value={formData.commentaires_reception || ''}
                  onChange={(e) => handleInputChange('commentaires_reception', e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Observations lors de la réception..."
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Paper elevation={2} sx={{ p: 3, mt: 4, backgroundColor: '#f8f9fa' }}>
          {errors.submit && (
            <Alert
              severity="error"
              sx={{ mb: 2, whiteSpace: 'pre-line' }}
              onClose={() => { setErrors(prev => ({ ...prev, submit: '' })); setSubmitError(null); }}
            >
              <AlertTitle>Échec de l'enregistrement</AlertTitle>
              {errors.submit}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSubmitting}
              size="large"
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || formData.selected_elements.length === 0}
              size="large"
              sx={{ minWidth: 200 }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {submitState.phase === 'printing'
                    ? 'Génération des étiquettes...'
                    : submitState.total > 1
                    ? `Création ${submitState.current}/${submitState.total}...`
                    : 'Enregistrement...'}
                </>
              ) : (
                'Enregistrer l\'Échantillon'
              )}
            </Button>
          </Stack>
        </Paper>

        {/* 🆕 Full-screen submission progress overlay */}
        <Backdrop
          open={submitState.active}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 10, color: '#fff' }}
        >
          <Paper elevation={6} sx={{ p: 4, width: 420, maxWidth: '90vw', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              {submitState.phase === 'printing'
                ? '🖨️ Génération des QR codes et étiquettes…'
                : '📝 Enregistrement des échantillons…'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {submitState.phase === 'printing'
                ? 'Création des codes QR et des étiquettes. Cela peut prendre quelques instants, merci de patienter.'
                : `Échantillon ${Math.min(submitState.current + (submitState.phase === 'creating' ? 1 : 0), submitState.total)} sur ${submitState.total}`}
            </Typography>
            {submitState.phase === 'creating' && submitState.total > 0 ? (
              <LinearProgress
                variant="determinate"
                value={Math.round((submitState.current / submitState.total) * 100)}
                sx={{ height: 10, borderRadius: 5 }}
              />
            ) : (
              <LinearProgress sx={{ height: 10, borderRadius: 5 }} />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Ne fermez pas cette fenêtre.
            </Typography>
          </Paper>
        </Backdrop>

        {/* 🆕 Submission error dialog (e.g. duplicate ID) */}
        <Dialog open={!!submitError} onClose={() => setSubmitError(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: 'error.main' }}>Échec de l'enregistrement</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {submitError}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmitError(null)} variant="contained">
              Fermer
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Batch Success Dialog */}
      <Dialog
        open={showBatchSuccess}
        onClose={() => setShowBatchSuccess(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
            <CheckCircleIcon sx={{ mr: 1 }} />
            🎉 Lot Créé avec Succès!
          </Typography>
        </DialogTitle>
        <DialogContent>
          {batchCreated && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>🎯 Lot {batchCreated.batch.batch_number} créé avec succès!</strong>
                </Typography>
                <Typography variant="body2">
                  {batchCreated.summary.totalSamples} échantillons ont été générés automatiquement 
                  avec leurs QR codes individuels pour traçabilité.
                </Typography>
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {batchCreated.summary.totalSamples}
                      </Typography>
                      <Typography variant="body2">
                        Échantillons créés
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="secondary" gutterBottom>
                        {batchCreated.summary.totalWeight.toFixed(3)} kg
                      </Typography>
                      <Typography variant="body2">
                        Poids total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="info.main" gutterBottom>
                        {batchCreated.summary.elementsSelected}
                      </Typography>
                      <Typography variant="body2">
                        Éléments sélectionnés
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="warning.main" gutterBottom>
                        {batchCreated.summary.methodsSelected}
                      </Typography>
                      <Typography variant="body2">
                        Méthodes d'analyse
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>📋 Prochaines étapes:</strong>
                  <br />• Les {batchCreated.summary.totalSamples} échantillons sont maintenant dans le système
                  <br />• Chaque échantillon a son QR code unique pour traçabilité
                  <br />• Ils suivront automatiquement le workflow défini selon les méthodes sélectionnées
                  <br />• Vous pouvez modifier individuellement chaque échantillon si nécessaire
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              // Print the reception summary
              if (batchCreated) {
                handlePrintSummary();
              }
            }}
            variant="outlined"
            startIcon={<PrintIcon />}
          >
            Imprimer Résumé
          </Button>
          <Button 
            onClick={() => {
              setShowBatchSuccess(false);
              setBatchCreated(null);
              onCancel(); // Close the form as well
            }} 
            variant="contained"
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🆕 Per-Sample Method Selection Modal */}
      <Dialog
        open={sampleMethodModalOpen}
        onClose={handleSampleMethodModalCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          backgroundColor: alpha(getLabColor(sampleMethodModalLab), 0.1),
          borderBottom: `3px solid ${getLabColor(sampleMethodModalLab)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon sx={{ color: getLabColor(sampleMethodModalLab) }} />
            <Box>
              <Typography variant="h6">
                Sélectionner la méthode pour {sampleMethodModalElement}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Échantillon {sampleMethodModalSampleNumber} - Laboratoire: {getLabDisplayName(sampleMethodModalLab)}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {sampleMethodModalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : sampleMethodModalMethods.length === 0 ? (
            <Alert severity="warning">
              Aucune méthode disponible pour {sampleMethodModalElement} dans ce laboratoire.
            </Alert>
          ) : (
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                Méthodes disponibles ({sampleMethodModalMethods.length})
              </FormLabel>
              <RadioGroup>
                {sampleMethodModalMethods.map((method) => {
                  const sample = individualSamples.find(s => s.sample_number === sampleMethodModalSampleNumber);
                  const isSelected = sample?.element_method_mapping?.[sampleMethodModalElement] === method.method_code;
                  return (
                    <Paper
                      key={method.method_code}
                      elevation={1}
                      sx={{
                        p: 2,
                        mb: 1,
                        cursor: 'pointer',
                        border: isSelected ? `2px solid ${getLabColor(sampleMethodModalLab)}` : '2px solid transparent',
                        '&:hover': {
                          backgroundColor: alpha(getLabColor(sampleMethodModalLab), 0.05),
                          borderColor: alpha(getLabColor(sampleMethodModalLab), 0.5)
                        },
                        transition: 'all 0.2s'
                      }}
                      onClick={() => handleSampleMethodSelect(sampleMethodModalSampleNumber, sampleMethodModalElement, sampleMethodModalLab, method.method_code)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Radio
                          value={method.method_code}
                          checked={isSelected}
                          sx={{ mt: -0.5 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {method.method_name}
                            </Typography>
                            {method.is_accredited && (
                              <Chip
                                label="✓ ACCRÉDITÉ"
                                size="small"
                                color="success"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {method.method_code}
                          </Typography>
                          {method.detection_limits && method.detection_limits[sampleMethodModalElement] && (
                            <Typography variant="caption" sx={{
                              backgroundColor: '#f5f5f5',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              display: 'inline-block',
                              mt: 0.5
                            }}>
                              Limite: {method.detection_limits[sampleMethodModalElement].limit} {method.detection_limits[sampleMethodModalElement].unit}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSampleMethodModalCancel} color="inherit">
            Annuler
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CompleteSampleReceptionForm; 